import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import jsPDF from 'jspdf';
import { getApiUrl, axiosConfig } from '../config/api';
import UpgradeModal from '../components/UpgradeModal';
import './KeywordAnalysis.css';
import '../components/SEOAnalyzer.css';

// Fun facts for loading overlays (same as Dashboard)
const funFacts = [
  "Did you know? Google processes over 8.5 billion searches per day!",
  "Did you know? 75% of users never scroll past the first page of search results.",
  "Did you know? Websites that load in 2 seconds have a 9% bounce rate, while those taking 5 seconds have a 38% bounce rate.",
  "Did you know? Mobile-first indexing means Google primarily uses the mobile version of your site for ranking.",
  "Did you know? Pages with images get 94% more views than those without.",
  "Did you know? The average user reads only 20% of the text on a webpage.",
  "Did you know? Websites with SSL certificates (HTTPS) rank higher in Google search results.",
  "Did you know? The first 3 seconds of page load time impact 53% of mobile site visits.",
  "Did you know? Internal linking can improve your site's SEO and help users navigate better.",
  "Did you know? Long-form content (2000+ words) tends to rank higher in search results.",
  "Did you know? Schema markup can help your website appear in rich snippets, increasing click-through rates.",
  "Did you know? Core Web Vitals are now a ranking factor for Google search results.",
  "Did you know? Websites with fast loading times have better conversion rates.",
  "Did you know? Alt text for images not only helps SEO but also improves accessibility.",
  "Did you know? A well-structured sitemap helps search engines crawl and index your site more effectively."
];

// Status Icon Component
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'good':
      return (
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="10" cy="10" r="9" opacity="0.25"></circle>
          <path d="M6 10l2.5 2.5L14 7"></path>
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      );
    case 'error':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      );
  }
};

// Circular Progress Component
const CircularProgress = ({ percentage, size = 80, strokeWidth = 8 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  
  const getColor = () => {
    if (percentage >= 80) return 'var(--success-color)';
    if (percentage >= 50) return 'var(--warning-color)';
    return 'var(--error-color)';
  };

  return (
    <div className="circular-progress-container" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="circular-progress-svg">
        <circle
          className="circular-progress-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <circle
          className="circular-progress-fg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          stroke={getColor()}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="circular-progress-text">
        <span className="circular-progress-percentage">{Math.round(percentage)}</span>
      </div>
    </div>
  );
};

// Detail Row Component
const DetailRow = ({ label, value, remark, status = 'good', additionalContent }) => {
  const resolvedValue =
    value !== undefined && value !== null && value !== ''
      ? value
      : <span className="muted">Not available</span>;

  return (
    <div className={`detail-row detail-${status}`}>
      <div className="row-status-section">
        <div className={`row-status-icon status-${status}`}>
          <StatusIcon status={status} />
        </div>
      </div>
      <div className="row-metric-section">
        <div className="row-label">{label}</div>
      </div>
      <div className="row-remark-section">
        <div className="combined-explanation">
          <span className="explanation-value">{resolvedValue}</span>
          {remark && (
            <span className="explanation-remark"> — {remark}</span>
          )}
        </div>
        {additionalContent && (
          <div className="additional-content">
            {additionalContent}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions to calculate scores
const calculateMetaScore = (results) => {
  let score = 0;
  let total = 4;
  
  // Title: keyword presence (0.5 points) + length (0.5 points) = 1 point total
  if (results.inTitle) score += 0.5;
  if (results.metaTitle && results.metaTitle.length >= MIN_TITLE && results.metaTitle.length <= MAX_TITLE) score += 0.5;
  
  // Description: keyword presence (0.5 points) + length (0.5 points) = 1 point total
  if (results.inDescription) score += 0.5;
  if (results.metaDescription && results.metaDescription.length >= MIN_DESCRIPTION && results.metaDescription.length <= MAX_DESCRIPTION) score += 0.5;
  
  // Indexing status (1 point)
  if (results.indexingStatus === 'index') score += 1;
  
  // OG Image (1 point, if available in audit results)
  if (results.auditResults?.meta?.ogImage) score += 1;
  
  return (score / total) * 100;
};

const calculateContentScore = (results) => {
  let score = 0;
  let total = 3;
  
  // Word count quality (1 point)
  if (results.contentWordCount >= 300) score += 1;
  else if (results.contentWordCount >= 150) score += 0.7;
  else if (results.contentWordCount > 0) score += 0.4;
  
  // Keyword in content (1 point)
  if (results.keywordCount > 0) score += 1;
  
  // Paragraph count (1 point - good if has paragraphs)
  const paragraphCount = results.auditResults?.content?.paragraphCount || 0;
  if (paragraphCount > 0) score += 1;
  
  return (score / total) * 100;
};

const calculateKeywordHeadingScore = (results) => {
  let score = 0;
  let total = 3;
  
  // Keyword in headings (1 point)
  if (results.inHeadings?.found) {
    const locations = results.inHeadings.locations || [];
    const h1Count = locations.filter(loc => loc.level.toLowerCase() === 'h1').length;
    const h2Count = locations.filter(loc => loc.level.toLowerCase() === 'h2').length;
    
    // Higher score if keyword is in H1 or multiple H2s
    if (h1Count > 0) score += 1;
    else if (h2Count >= 2) score += 0.9;
    else if (h2Count === 1) score += 0.8;
    else score += 0.7; // In other headings
  }
  
  // H1 structure (1 point)
  const headings = results.auditResults?.headings || {};
  const h1Count = headings.h1Count || 0;
  if (h1Count === 1) score += 1;
  else if (h1Count > 1) score += 0.5;
  // 0 if no H1
  
  // H2 structure (1 point)
  const h2Count = headings.h2?.length || 0;
  if (h2Count > 0) score += 1;
  
  return (score / total) * 100;
};

const calculateKeywordDensityScore = (density) => {
  const densityNum = parseFloat(density) || 0;
  if (densityNum >= 1 && densityNum <= 2) return 100;
  if (densityNum > 0 && densityNum < 1) return 30;
  if (densityNum > 2 && densityNum < 3) return 70;
  if (densityNum >= 3) return 30;
  return 0;
};

const calculateImageScore = (results) => {
  if (results.totalImages === 0) return 50; // No images, neutral score
  if (results.imagesWithKeyword === 0) return 30; // Images but no keyword in alt
  const ratio = (results.imagesWithKeyword / results.totalImages) * 100;
  if (ratio >= 50) return 100;
  if (ratio >= 25) return 70;
  return 50;
};

const calculateUrlScore = (results) => {
  return results.inUrl ? 100 : 0;
};

const URL_STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'the',
  'or',
  'for',
  'of',
  'in',
  'on',
  'to',
  'with',
  'by',
  'is',
  'are',
  'be',
  'this',
  'that',
  'these',
  'those',
  'at',
  'from'
]);

const normalizeUrlSegment = (value = '') =>
  value
    .toLowerCase()
    .replace(/https?:\/\//, '')
    .replace(/[?#].*$/, '')
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]/g, ' ')
    .replace(/\/+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const splitIntoKeywordWords = (value = '') =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s-_/]/g, ' ')
    .split(/\s+/)
    .filter((word) => word && word.length > 1 && !URL_STOP_WORDS.has(word));

const isHomepage = (url) => {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();
    // Check if pathname is empty, just "/", or contains only index files
    const isRoot = pathname === '' || pathname === '/';
    const isIndexFile = /^\/index\.(html|htm|php|asp|aspx)$/i.test(pathname);
    return isRoot || isIndexFile;
  } catch (e) {
    return false;
  }
};

const isKeywordInUrl = (url, keyword) => {
  if (!url || !keyword) return false;

  try {
    const decodedUrl = decodeURIComponent(url);
    const urlObj = new URL(decodedUrl);
    const combined = `${urlObj.hostname}${urlObj.pathname}`;
    let normalizedPath = normalizeUrlSegment(combined);

    const keywordWords = splitIntoKeywordWords(keyword);
    if (!keywordWords.length) return false;

    const pathWords = normalizedPath.split(' ').filter(Boolean);
    const allWordsFound =
      pathWords.length > 0 &&
      keywordWords.every((kw) =>
        pathWords.some((pw) => pw.includes(kw) || kw.includes(pw))
      );

    if (allWordsFound) {
      return true;
    }

    const keywordPattern = keywordWords
      .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .join('[-_/\\s]+');

    const slugRegex = new RegExp(keywordPattern, 'i');
    return slugRegex.test(normalizedPath);
  } catch (error) {
    const fallback = url.toLowerCase();
    const keywordWords = splitIntoKeywordWords(keyword);
    return keywordWords.some((word) => fallback.includes(word));
  }
};

// Constants for site audit
const MIN_TITLE = 30;
const MAX_TITLE = 60;
const MIN_DESCRIPTION = 120;
const MAX_DESCRIPTION = 160;

// Helper functions for site audit sections
// eslint-disable-next-line no-unused-vars
const formatList = (items = []) => {
  if (!items.length) return <span className="muted">No data</span>;
  return (
    <ul className="inline-list">
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
};

const formatTags = (tags = []) => {
  if (!tags.length) return <span className="muted">No data</span>;
  return (
    <div className="tag-container">
      {tags.map((tag, idx) => (
        <span key={idx} className="schema-tag">
          {tag.replace('https://schema.org/', '').replace('http://schema.org/', '')}
        </span>
      ))}
    </div>
  );
};

const formatHeadings = (headings = []) => {
  if (!headings.length) return <span className="muted">No data</span>;
  return (
    <div className="heading-container">
      {headings.map((item, idx) => (
        <div key={idx} className="heading-box">
          {item}
        </div>
      ))}
    </div>
  );
};

const formatLinkList = (links = []) => {
  if (!links.length) return <span className="muted">No data</span>;

  return (
    <div className="link-list-container">
      <div className="link-preview-item link-preview-header">
        <div className="link-preview-section link-preview-type">
          <span className="link-header-label">Link Type</span>
        </div>
        <div className="link-preview-section link-preview-anchor">
          <span className="link-header-label">Anchor Text</span>
        </div>
        <div className="link-preview-section link-preview-url">
          <span className="link-header-label">URL</span>
        </div>
      </div>
      {links.map((link, idx) => {
        const isNofollow = link.isNofollow;
        const isExternal = link.isExternal;
        const anchorText = link.anchor || link.text || '';
        const href = link.href || link.url || '';

        const tagLabel = link.tagLabelOverride
          ? link.tagLabelOverride
          : isNofollow
          ? 'Nofollow'
          : isExternal
          ? 'External'
          : 'Internal';

        const tagClass = link.tagClassOverride
          ? link.tagClassOverride
          : isNofollow
          ? 'tag-nofollow'
          : isExternal
          ? 'tag-external'
          : 'tag-internal';

        return (
          <div key={idx} className="link-preview-item link-preview-row">
            <div className="link-preview-section link-preview-type">
              <span className={`link-type-tag ${tagClass}`}>{tagLabel}</span>
            </div>
            <div className="link-preview-section link-preview-anchor">
              {link.image ? (
                <div className="link-image-anchor">
                  <img src={link.image} alt="Link preview" onError={(e) => (e.target.style.display = 'none')} />
                  <span>Image Link</span>
                </div>
              ) : (
                <span className="link-anchor">
                  {anchorText && anchorText.trim() ? anchorText : '-'}
                </span>
              )}
            </div>
            <div className="link-preview-section link-preview-url">
              {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer" className="link-url">
                  {href}
                </a>
              ) : (
                <span className="muted">No URL</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const formatImageList = (images, options = {}) => {
  const { showSize = false, remarkText = '' } = options;
  if (!images.length) return <span className="muted">No data</span>;

  const getImageTitle = (img, src) => {
    if (typeof img === 'object' && img.title && img.title.trim()) {
      return img.title;
    }
    try {
      const url = new URL(src);
      const pathname = url.pathname;
      const filename = pathname.split('/').pop();
      if (filename && filename.includes('.')) {
        const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
        if (nameWithoutExt) {
          return decodeURIComponent(nameWithoutExt)
            .replace(/[-_]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-useless-escape
      const match = src.match(/\/([^\/]+\.(jpg|jpeg|png|gif|webp|svg))$/i);
      if (match && match[1]) {
        return match[1].split('.')[0].replace(/[-_]/g, ' ').trim();
      }
    }
    return null;
  };

  return (
    <div className="image-list-container">
      <div className="image-preview-item image-preview-header">
        <div className="image-preview-section image-preview-left">
          <span className="image-header-label">Image</span>
        </div>
        <div className="image-preview-section image-preview-info">
          <span className="image-header-label">Details</span>
        </div>
        <div className="image-preview-section image-preview-remark">
          <span className="image-header-label">Remarks</span>
        </div>
      </div>
      {images.map((img, idx) => {
        const src = typeof img === 'string' ? img : img.src;
        let title = typeof img === 'object' && img.title ? img.title : null;
        if (!title || !title.trim()) {
          title = getImageTitle(img, src);
        }
        const size = typeof img === 'object' && img.size ? img.size : null;
        const resolvedRemark =
          typeof remarkText === 'function' ? remarkText(img) : remarkText || '';

        return (
          <div key={idx} className="image-preview-item image-preview-row">
            <div className="image-preview-section image-preview-left">
              <img
                src={src}
                alt="Preview"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
                className="image-thumbnail"
              />
              <div className="image-placeholder" style={{ display: 'none' }}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
              </div>
            </div>
            <div className="image-preview-section image-preview-info">
              {title && title.trim() && (
                <div className="image-title">{title}</div>
              )}
              <a href={src} target="_blank" rel="noopener noreferrer" className="image-link">
                {src}
              </a>
              {showSize && size && <span className="image-size">{size}</span>}
            </div>
            <div className="image-preview-section image-preview-remark">
              <div className="image-remark-label">Recommended fix</div>
              <p className="image-remark-text">
                {resolvedRemark || 'Review this asset for potential improvements.'}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getLengthFeedback = (text, min, max, label) => {
  const labelLower = label.toLowerCase();
  if (!text) {
    return {
      remark: `You don't have a ${labelLower} set. This is the text that appears in search results and browser tabs. Add one that describes your page clearly and includes important keywords.`,
      status: 'warning'
    };
  }
  if (text.length < min) {
    return {
      remark: `Your ${labelLower} is too short (${text.length} characters). Search engines prefer ${min}–${max} characters.`,
      status: 'warning'
    };
  }
  if (text.length > max) {
    return {
      remark: `Your ${labelLower} is too long (${text.length} characters). Search engines typically cut off text after ${max} characters.`,
      status: 'warning'
    };
  }
  return {
    remark: `Your ${labelLower} is the perfect length (${text.length} characters).`,
    status: 'good'
  };
};

const getRobotsRemark = (robots) => {
  if (!robots.exists) {
    return {
      remark:
        "Your website doesn't have a robots.txt file. This file tells search engines which pages they can and cannot visit.",
      status: 'warning'
    };
  }
  if (!robots.accessible) {
    return {
      remark:
        "Your robots.txt file exists but search engines can't access it. This might be due to server settings or permissions.",
      status: 'warning'
    };
  }
  return {
    remark:
      'Your robots.txt file is working properly and search engines can access it. Make sure the rules inside match what you want search engines to see.',
    status: 'good'
  };
};

const getSchemaRemark = (schema) => {
  if (!schema.found) {
    return {
      remark:
        "Your website doesn't use structured data (schema markup). This special code helps search engines understand your content better.",
      status: 'warning'
    };
  }
  return {
    remark: `Great! Your website uses structured data (${schema.types.length || 1} type${
      schema.types.length !== 1 ? 's' : ''
    }).`,
    status: 'good'
  };
};

const getSitemapRemark = (sitemaps) => {
  if (!sitemaps.found) {
    return {
      remark:
        'No sitemap found. A sitemap is like a map of your website that helps search engines discover all your pages.',
      status: 'warning'
    };
  }
  return {
    remark: `Good! You have ${sitemaps.sitemapUrls.length} sitemap${
      sitemaps.sitemapUrls.length !== 1 ? 's' : ''
    } that help search engines find your pages.`,
    status: 'good'
  };
};

const getContentRemark = (content) => {
  if (content.wordCount < 300) {
    return {
      remark: `Your page has ${content.wordCount} words, which is quite short.`,
      status: 'warning'
    };
  }
  return {
    remark: `Your page has ${content.wordCount} words, which is a good amount of content.`,
    status: 'good'
  };
};

const getImageRemark = (images) => {
  if (images.withoutAlt > 0) {
    return {
      remark: `${images.withoutAlt} of your images don't have alt text.`,
      status: 'warning'
    };
  }
  return {
    remark: 'Excellent! All your images have alt text.',
    status: 'good'
  };
};

const getLinkRemark = (links) => {
  if (links.external > links.internal) {
    return {
      remark:
        'You have more links pointing to other websites than links to other pages on your own site.',
      status: 'warning'
    };
  }
  return {
    remark: 'You have a good balance of internal and external links.',
    status: 'good'
  };
};

const calculateSecurityScore = (security) => {
  let score = 0;
  let total = 3;
  if (security.sslCertificate) score += 1;
  if (security.httpsRedirection) score += 1;
  if (security.wwwRedirection?.redirected) score += 1;
  return (score / total) * 100;
};

const calculateRobotsScore = (robots) => {
  if (!robots.exists) return 0;
  if (!robots.accessible) return 50;
  return 100;
};

const calculateSchemaSitemapScore = (schema, sitemaps) => {
  let score = 0;
  let total = 2;
  if (schema.found) score += 1;
  if (sitemaps.found) score += 1;
  return (score / total) * 100;
};

// eslint-disable-next-line no-unused-vars
const calculateAuditMetaScore = (meta) => {
  let score = 0;
  let total = 4;
  if (meta.title && meta.title.length >= MIN_TITLE && meta.title.length <= MAX_TITLE) score += 1;
  if (
    meta.description &&
    meta.description.length >= MIN_DESCRIPTION &&
    meta.description.length <= MAX_DESCRIPTION
  )
    score += 1;
  if (meta.indexingStatus === 'index') score += 1;
  if (meta.ogImage) score += 1;
  return (score / total) * 100;
};

// eslint-disable-next-line no-unused-vars
const calculateTechnicalScore = (meta) => {
  let score = 0;
  let total = 3;
  if (meta.viewport) score += 1;
  if (meta.charset) score += 1;
  if (meta.language) score += 1;
  return (score / total) * 100;
};

// eslint-disable-next-line no-unused-vars
const calculateHeadingScore = (headings) => {
  if (headings.h1Count === 0) return 0;
  if (headings.h1Count > 1) return 50;
  if (headings.h2.length === 0) return 60;
  return 100;
};

// eslint-disable-next-line no-unused-vars
const calculateKeywordScore = (keywords) => {
  return keywords.topKeywords.length > 0 ? 100 : 50;
};

// eslint-disable-next-line no-unused-vars
const calculateAuditContentScore = (content) => {
  if (content.wordCount >= 300) return 100;
  if (content.wordCount >= 150) return 70;
  return 40;
};

// eslint-disable-next-line no-unused-vars
const calculateAuditImageScore = (images) => {
  if (images.total === 0) return 100;
  let score = 0;
  let total = 3;
  if (images.withoutAlt === 0) score += 1;
  else if (images.withAlt > 0) score += images.withAlt / images.total;
  if (images.largeImages === 0) score += 1;
  else if (images.largeImages < images.total) score += (images.total - images.largeImages) / images.total;
  if (!images.missingWebpList || images.missingWebpList.length === 0) score += 1;
  else if (images.missingWebpList.length < images.total) score +=
      (images.total - images.missingWebpList.length) / images.total;
  return (score / total) * 100;
};

const calculateAuditLinkScore = (links) => {
  if (links.total === 0) return 50;
  if (links.internal >= links.external) return 100;
  return 70;
};

const KeywordAnalysis = ({ user, onLogout, currentProject }) => {
  const { updateProject, userPlan } = useProject();
  const { user: authUser } = useAuth();
  const reportRef = useRef(null);
  const [keywords, setKeywords] = useState([]); // Array of { id, keyword, targetUrl, results, timestamp }
  const [selectedKeywordId, setSelectedKeywordId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingKeywordId, setDeletingKeywordId] = useState(null);
  const [deletingKeywordName, setDeletingKeywordName] = useState('');
  const [modalKeyword, setModalKeyword] = useState('');
  const [modalTargetUrl, setModalTargetUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState('light');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [funFact, setFunFact] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(true); // Track credits loading state
  // eslint-disable-next-line no-unused-vars
  const [keywordCredits, setKeywordCredits] = useState({ creditsRemaining: -1, totalUsed: 0, limit: -1 });
  const [recentlyDeletedKeyword, setRecentlyDeletedKeyword] = useState(null); // Store recently deleted keyword data
  const [analyzeCountdown, setAnalyzeCountdown] = useState(0);
  const [downloadCountdown, setDownloadCountdown] = useState(0);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Helper function to get cache key for a project
  const getCacheKey = (projectUrl) => {
    if (!projectUrl) return null;
    // Create a cache key based on the URL
    return `keywordAnalyses_${projectUrl}`;
  };

  // Helper function to get countdown key for analyze action
  const getAnalyzeCountdownKey = (projectUrl) => {
    if (!projectUrl) return null;
    return `keywordAnalysis_countdown_analyze_${projectUrl}`;
  };

  // Helper function to get countdown key for download action
  const getDownloadCountdownKey = (projectUrl) => {
    if (!projectUrl) return null;
    return `keywordAnalysis_countdown_download_${projectUrl}`;
  };

  // Helper function to get recently deleted keyword storage key
  const getRecentlyDeletedKey = (projectUrl) => {
    if (!projectUrl) return null;
    return `recentlyDeletedKeyword_${projectUrl}`;
  };

  // Load keyword analysis credits (only for basic plan users, non-blocking)
  useEffect(() => {
    // Only load credits for basic plan users
    if (userPlan !== 'basic') {
      setCreditsLoading(false);
      setKeywordCredits({ creditsRemaining: -1, totalUsed: 0, limit: -1 });
      return;
    }

    const loadCredits = async () => {
      setCreditsLoading(true);
      try {
        const token = localStorage.getItem('scanmaster_token');
        if (!token) {
          setCreditsLoading(false);
          return;
        }

        const response = await fetch(getApiUrl('keyword-analyses/credits'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setKeywordCredits(data);
        }
      } catch (err) {
        console.error('Error loading keyword credits:', err);
      } finally {
        setCreditsLoading(false);
      }
    };

    loadCredits();
  }, [userPlan, currentProject?.id]); // Reload when plan or project changes

  // Check analyze countdown on mount and when project changes
  useEffect(() => {
    const targetUrl = currentProject?.url || null;
    if (!targetUrl) {
      setAnalyzeCountdown(0);
      return;
    }

    const analyzeKey = getAnalyzeCountdownKey(targetUrl);
    if (!analyzeKey) {
      setAnalyzeCountdown(0);
      return;
    }

    const savedCountdownEnd = localStorage.getItem(analyzeKey);
    if (savedCountdownEnd) {
      const endTime = parseInt(savedCountdownEnd, 10);
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      
      if (remaining > 0) {
        setAnalyzeCountdown(remaining);
      } else {
        localStorage.removeItem(analyzeKey);
        setAnalyzeCountdown(0);
      }
    } else {
      setAnalyzeCountdown(0);
    }
  }, [currentProject?.url]);

  // Check download countdown on mount and when project changes
  useEffect(() => {
    const targetUrl = currentProject?.url || null;
    if (!targetUrl) {
      setDownloadCountdown(0);
      return;
    }

    const downloadKey = getDownloadCountdownKey(targetUrl);
    if (!downloadKey) {
      setDownloadCountdown(0);
      return;
    }

    const savedCountdownEnd = localStorage.getItem(downloadKey);
    if (savedCountdownEnd) {
      const endTime = parseInt(savedCountdownEnd, 10);
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      
      if (remaining > 0) {
        setDownloadCountdown(remaining);
      } else {
        localStorage.removeItem(downloadKey);
        setDownloadCountdown(0);
      }
    } else {
      setDownloadCountdown(0);
    }
  }, [currentProject?.url]);

  // Analyze countdown timer effect
  useEffect(() => {
    if (analyzeCountdown <= 0) return;
    
    const targetUrl = currentProject?.url || null;
    const analyzeKey = getAnalyzeCountdownKey(targetUrl);
    if (!analyzeKey) return;

    const timer = setInterval(() => {
      setAnalyzeCountdown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem(analyzeKey);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [analyzeCountdown, currentProject?.url]);

  // Download countdown timer effect
  useEffect(() => {
    if (downloadCountdown <= 0) return;
    
    const targetUrl = currentProject?.url || null;
    const downloadKey = getDownloadCountdownKey(targetUrl);
    if (!downloadKey) return;

    const timer = setInterval(() => {
      setDownloadCountdown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem(downloadKey);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [downloadCountdown, currentProject?.url]);

  // Load saved keyword analyses from localStorage when component mounts or project changes
  useEffect(() => {
    if (!currentProject) {
      setKeywords([]);
      setSelectedKeywordId(null);
      setRecentlyDeletedKeyword(null);
      setIsInitialLoad(false);
      return;
    }

    const projectUrl = currentProject.url;
    if (!projectUrl) {
      setKeywords([]);
      setSelectedKeywordId(null);
      setModalTargetUrl('');
      setRecentlyDeletedKeyword(null);
      setIsInitialLoad(false);
      return;
    }

    // Load recently deleted keyword from Supabase (cross-device support)
    const loadRecentlyDeletedKeyword = async () => {
      if (!authUser || !currentProject) return;
      
      try {
        // Get user ID from Supabase session
        const { data: { user: supabaseUser } } = await supabase.auth.getUser();
        if (!supabaseUser) return;

        // Query for the most recently deleted keyword for this user and project
        const { data, error } = await supabase
          .from('recently_deleted_keywords')
          .select('keyword_data, id')
          .eq('user_id', supabaseUser.id)
          .eq('project_id', currentProject.id)
          .order('deleted_at', { ascending: false })
          .limit(1)
          .single();

        if (!error && data && data.keyword_data) {
          setRecentlyDeletedKeyword(data.keyword_data);
        }
      } catch (err) {
        console.error('Error loading recently deleted keyword from Supabase:', err);
        // Fallback to localStorage
        const recentlyDeletedKey = getRecentlyDeletedKey(projectUrl);
        if (recentlyDeletedKey) {
          try {
            const savedDeleted = localStorage.getItem(recentlyDeletedKey);
            if (savedDeleted) {
              const parsed = JSON.parse(savedDeleted);
              setRecentlyDeletedKeyword(parsed);
            }
          } catch (localErr) {
            console.error('Error loading recently deleted keyword from localStorage:', localErr);
          }
        }
      }
    };

    loadRecentlyDeletedKeyword();

    // Try to load from localStorage cache first
    const cacheKey = getCacheKey(projectUrl);
    let loadedKeywords = null;

    if (cacheKey) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          const cacheTimestamp = parsed.cachedAt || parsed.timestamp;
          
          if (cacheTimestamp) {
            const savedTime = new Date(cacheTimestamp);
            const now = new Date();
            const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
            
            // Use cached data if less than 24 hours old
            if (hoursDiff < 24 && Array.isArray(parsed.data)) {
              loadedKeywords = parsed.data;
            } else {
              // Cache expired, remove it
              localStorage.removeItem(cacheKey);
            }
          } else if (Array.isArray(parsed.data)) {
            // Legacy cache without timestamp, use it but mark for refresh
            loadedKeywords = parsed.data;
          }
        }
      } catch (err) {
        console.error('Error loading cached keyword analyses:', err);
        // Remove corrupted cache
        if (cacheKey) localStorage.removeItem(cacheKey);
      }
    }

    // Use cached data if available, otherwise fallback to project's keywordAnalyses
    // Load keywords synchronously first for immediate display
    if (loadedKeywords) {
      setKeywords(loadedKeywords);
      if (loadedKeywords.length > 0) {
        setSelectedKeywordId(prev => prev || loadedKeywords[0].id);
      } else {
        setSelectedKeywordId(null);
      }
    } else if (currentProject.keywordAnalyses) {
      setKeywords(currentProject.keywordAnalyses);
      if (currentProject.keywordAnalyses.length > 0) {
        setSelectedKeywordId(prev => prev || currentProject.keywordAnalyses[0].id);
      } else {
        setSelectedKeywordId(null);
      }
    } else {
      setKeywords([]);
      setSelectedKeywordId(null);
    }

    setModalTargetUrl(projectUrl);
    // Set isInitialLoad to false immediately after loading keywords (synchronously)
    // This allows UI to render immediately while async operations continue in background
    setIsInitialLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  // Save keywords to localStorage whenever they change (but not during initial load)
  useEffect(() => {
    if (!isInitialLoad && currentProject && currentProject.url) {
      const cacheKey = getCacheKey(currentProject.url);
      if (cacheKey && keywords.length >= 0) {
        try {
          const cacheData = {
            data: keywords,
            cachedAt: new Date().toISOString(),
            timestamp: new Date().toISOString()
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (err) {
          console.error('Error saving keyword analyses to cache:', err);
        }
      }
    }
  }, [keywords, isInitialLoad, currentProject]);

  // Update modal target URL when project changes
  useEffect(() => {
    if (currentProject && !modalTargetUrl) {
      setModalTargetUrl(currentProject.url || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  // eslint-disable-next-line no-unused-vars
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const handleOpenAddModal = () => {
    // For basic plan users: check if they already have 1 keyword (instant check)
    if (userPlan === 'basic' && keywords.length >= 1) {
      setShowUpgradeModal(true);
      return;
    }
    
    // For basic plan users: check if all credits are exhausted
    if (userPlan === 'basic' && keywordCredits.creditsRemaining === 0) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Open modal immediately (no API delay)
    setModalKeyword('');
    setModalTargetUrl(currentProject?.url || '');
    setShowAddModal(true);
    setError(null);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setModalKeyword('');
    setModalTargetUrl(currentProject?.url || '');
    setError(null);
  };

  const handleAddKeyword = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!modalKeyword || !modalKeyword.trim()) {
      setError('Please enter a keyword');
      return;
    }

    if (!modalTargetUrl || !modalTargetUrl.trim()) {
      setError('Please enter a target URL');
      return;
    }

    // For basic plan users: check if they already have 1 keyword (safety check)
    if (userPlan === 'basic' && keywords.length >= 1) {
      setError('Basic plan allows only 1 keyword at a time. Please delete the existing keyword or upgrade to add more.');
      setShowAddModal(false);
      setShowUpgradeModal(true);
      return;
    }

    // Show full-page loading screen
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setFunFact(randomFact);
    setLoadingMessage('Analyzing keyword...');
    setShowLoadingScreen(true);

    setLoading(true);
    setError(null);

    try {
      // Validate URL format
      let url = modalTargetUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }

      // Validate that the target URL belongs to one of the user's project domains
      let urlObj;
      try {
        urlObj = new URL(url);
      } catch (e) {
        setError('Please enter a valid URL');
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      const targetDomain = urlObj.hostname.toLowerCase();
      const targetOrigin = urlObj.origin.toLowerCase();

      // Validate that the target URL belongs to the currently selected project
      if (!currentProject || !currentProject.url) {
        setError('Please select a project first.');
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      // Normalize project URL (add protocol if missing)
      let projectUrl = currentProject.url.trim();
      if (!projectUrl.startsWith('http://') && !projectUrl.startsWith('https://')) {
        projectUrl = 'https://' + projectUrl;
      }

      let belongsToProject = false;
      try {
        const projectUrlObj = new URL(projectUrl);
        const projectDomain = projectUrlObj.hostname.toLowerCase();
        const projectOrigin = projectUrlObj.origin.toLowerCase();
        
        // Check if domains match (allows subdomains)
        // For example: www.example.com matches example.com
        // But example.com does NOT match other-example.com
        belongsToProject = targetDomain === projectDomain || 
               targetDomain.endsWith('.' + projectDomain) ||
               projectDomain.endsWith('.' + targetDomain) ||
               targetOrigin === projectOrigin;
      } catch (e) {
        setError('Invalid project URL format.');
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      if (!belongsToProject) {
        setError(`The target URL must belong to the selected project domain (${currentProject.url}). Please use a URL from this project.`);
        setLoading(false);
        setShowLoadingScreen(false);
        return;
      }

      // Call the analyze endpoint and extract keyword-specific data
      const response = await axios.post(getApiUrl('analyze'), { url }, axiosConfig);
      const analysisData = response.data;

      // Extract keyword-specific information
      const keywordLower = modalKeyword.toLowerCase().trim();
      const keywordText = modalKeyword.trim();
      const keywordId = Date.now().toString(); // Simple ID generation
      
      // Check images for keyword in alt text and title
      const imagesWithKeyword = (analysisData.images?.imageList || []).filter(img => {
        const altText = (img.alt || '').toLowerCase();
        const titleText = (img.title || '').toLowerCase();
        // Use word boundary matching for more accurate results
        const keywordRegex = new RegExp(`\\b${keywordLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        return keywordRegex.test(altText) || keywordRegex.test(titleText);
      });
      
      // Check if keyword is in URL
      const keywordInUrl = isKeywordInUrl(url, keywordText);
      
      const keywordResults = {
        keyword: keywordText,
        targetUrl: url,
        timestamp: new Date().toISOString(),
        // Check if keyword appears in various places
        inTitle: analysisData.meta?.title?.toLowerCase().includes(keywordLower) || false,
        inDescription: analysisData.meta?.description?.toLowerCase().includes(keywordLower) || false,
        inUrl: keywordInUrl,
        inHeadings: checkKeywordInHeadings(analysisData.headings, keywordLower),
        inContent: checkKeywordInContent(analysisData.content, keywordLower),
        keywordDensity: calculateKeywordDensity(analysisData.content, keywordLower),
        keywordCount: countKeywordOccurrences(analysisData.content, keywordLower),
        keywordPositions: findKeywordPositions(analysisData.content, keywordLower),
        // Overall SEO data for context
        metaTitle: analysisData.meta?.title || '',
        metaDescription: analysisData.meta?.description || '',
        metaRobots: analysisData.meta?.robots || '',
        indexingStatus: analysisData.meta?.indexingStatus || 'unknown',
        headings: analysisData.headings || {},
        contentWordCount: analysisData.content?.wordCount || 0,
        // Image analysis
        totalImages: analysisData.images?.total || 0,
        imagesWithKeyword: imagesWithKeyword.length,
        imagesWithKeywordList: imagesWithKeyword,
        // Store full audit results for the page audit section
        auditResults: {
          security: analysisData.security,
          robots: analysisData.robots,
          schema: analysisData.schema,
          sitemaps: analysisData.sitemaps,
          meta: analysisData.meta,
          headings: analysisData.headings,
          content: analysisData.content,
          images: analysisData.images,
          links: analysisData.links,
        },
      };

      const newKeyword = {
        id: keywordId,
        keyword: keywordText,
        targetUrl: url,
        results: keywordResults,
        timestamp: new Date().toISOString(),
      };

      // Record keyword analysis in database (for credit tracking)
      try {
        const token = localStorage.getItem('scanmaster_token');
        if (token) {
          const recordResponse = await fetch(getApiUrl('keyword-analyses'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              keyword: keywordText,
              target_url: url,
              project_id: currentProject?.id || null
            })
          });

          if (recordResponse.ok) {
            const recordData = await recordResponse.json();
            // Update credits state
            if (recordData.creditsRemaining !== undefined) {
              setKeywordCredits({
                creditsRemaining: recordData.creditsRemaining,
                totalUsed: recordData.totalUsed,
                limit: recordData.limit
              });
            }
          } else if (recordResponse.status === 403) {
            // Credits exhausted
            const errorData = await recordResponse.json();
            setError(errorData.error || 'You have reached the limit of 3 keyword analyses for Basic plan.');
            setShowAddModal(false);
            setShowUpgradeModal(true);
            setShowLoadingScreen(false);
            setLoading(false);
            return;
          }
        }
      } catch (recordErr) {
        console.error('Error recording keyword analysis:', recordErr);
        // Continue anyway - don't block the user if recording fails
      }

      const updatedKeywords = [...keywords, newKeyword];
      setKeywords(updatedKeywords);
      setSelectedKeywordId(keywordId);
      setShowAddModal(false);
      setModalKeyword('');
      setModalTargetUrl(currentProject?.url || '');
      // Clear recently deleted keyword when a new keyword is added
      setRecentlyDeletedKeyword(null);
      // Clear from Supabase
      const clearFromSupabase = async () => {
        if (!authUser || !currentProject) return;
        
        try {
          const { data: { user: supabaseUser } } = await supabase.auth.getUser();
          if (!supabaseUser) return;

          await supabase
            .from('recently_deleted_keywords')
            .delete()
            .eq('user_id', supabaseUser.id)
            .eq('project_id', currentProject.id);
        } catch (err) {
          console.error('Error clearing recently deleted keyword from Supabase:', err);
        }
      };
      clearFromSupabase();
      // Clear from localStorage
      if (currentProject && currentProject.url) {
        const recentlyDeletedKey = getRecentlyDeletedKey(currentProject.url);
        if (recentlyDeletedKey) {
          try {
            localStorage.removeItem(recentlyDeletedKey);
          } catch (err) {
            console.error('Error clearing recently deleted keyword from localStorage:', err);
          }
        }
      }

      // Save to localStorage cache
      if (currentProject && currentProject.url) {
        const cacheKey = getCacheKey(currentProject.url);
        if (cacheKey) {
          try {
            const cacheData = {
              data: updatedKeywords,
              cachedAt: new Date().toISOString(),
              timestamp: new Date().toISOString()
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          } catch (err) {
            console.error('Error saving keyword analyses to cache:', err);
          }
        }
      }

      // Save to project
      if (currentProject) {
        updateProject(currentProject.id, {
          keywordAnalyses: updatedKeywords,
          lastKeywordAnalysisDate: new Date().toISOString()
        });
      }
    } catch (err) {
      console.error('Keyword analysis error:', err);
      let errorMessage = 'Failed to analyze keyword. Please try again.';
      
      if (err.response) {
        // Server responded with error status
        errorMessage = err.response.data?.error || `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Unable to connect to server. Please make sure the backend server is running on port 5001.';
      } else {
        // Error setting up the request
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setShowLoadingScreen(false);
    }
  };

  const handleReAnalyze = async (keywordId) => {
    const keywordData = keywords.find(k => k.id === keywordId);
    if (!keywordData) return;

    // Prevent analyze during countdown
    if (analyzeCountdown > 0) return;

    // Show full-page loading screen
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setFunFact(randomFact);
    setLoadingMessage('Re-analyzing keyword...');
    setShowLoadingScreen(true);
    setLoading(true);
    setError(null);

    try {
      // Validate URL format
      let url = keywordData.targetUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }

      // Call the analyze endpoint and extract keyword-specific data
      const response = await axios.post(getApiUrl('analyze'), { url }, axiosConfig);
      const analysisData = response.data;

      // Extract keyword-specific information
      const keywordLower = keywordData.keyword.toLowerCase().trim();
      
      // Check images for keyword in alt text and title
      const imagesWithKeyword = (analysisData.images?.imageList || []).filter(img => {
        const altText = (img.alt || '').toLowerCase();
        const titleText = (img.title || '').toLowerCase();
        // Use word boundary matching for more accurate results
        const keywordRegex = new RegExp(`\\b${keywordLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
        return keywordRegex.test(altText) || keywordRegex.test(titleText);
      });
      
      // Check if keyword is in URL
      const keywordInUrl = isKeywordInUrl(url, keywordData.keyword);
      
      const keywordResults = {
        keyword: keywordData.keyword,
        targetUrl: url,
        timestamp: new Date().toISOString(),
        inTitle: analysisData.meta?.title?.toLowerCase().includes(keywordLower) || false,
        inDescription: analysisData.meta?.description?.toLowerCase().includes(keywordLower) || false,
        inUrl: keywordInUrl,
        inHeadings: checkKeywordInHeadings(analysisData.headings, keywordLower),
        inContent: checkKeywordInContent(analysisData.content, keywordLower),
        keywordDensity: calculateKeywordDensity(analysisData.content, keywordLower),
        keywordCount: countKeywordOccurrences(analysisData.content, keywordLower),
        keywordPositions: findKeywordPositions(analysisData.content, keywordLower),
        metaTitle: analysisData.meta?.title || '',
        metaDescription: analysisData.meta?.description || '',
        metaRobots: analysisData.meta?.robots || '',
        indexingStatus: analysisData.meta?.indexingStatus || 'unknown',
        headings: analysisData.headings || {},
        contentWordCount: analysisData.content?.wordCount || 0,
        // Image analysis
        totalImages: analysisData.images?.total || 0,
        imagesWithKeyword: imagesWithKeyword.length,
        imagesWithKeywordList: imagesWithKeyword,
        // Store full audit results for the page audit section
        auditResults: {
          security: analysisData.security,
          robots: analysisData.robots,
          schema: analysisData.schema,
          sitemaps: analysisData.sitemaps,
          meta: analysisData.meta,
          headings: analysisData.headings,
          content: analysisData.content,
          images: analysisData.images,
          links: analysisData.links,
        },
      };

      const updatedKeywords = keywords.map(k => 
        k.id === keywordId 
          ? { ...k, results: keywordResults, timestamp: new Date().toISOString() }
          : k
      );
      setKeywords(updatedKeywords);

      // Save to localStorage cache
      if (currentProject && currentProject.url) {
        const cacheKey = getCacheKey(currentProject.url);
        if (cacheKey) {
          try {
            const cacheData = {
              data: updatedKeywords,
              cachedAt: new Date().toISOString(),
              timestamp: new Date().toISOString()
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
          } catch (err) {
            console.error('Error saving keyword analyses to cache:', err);
          }
        }
      }

      // Save to project
      if (currentProject) {
        updateProject(currentProject.id, {
          keywordAnalyses: updatedKeywords,
          lastKeywordAnalysisDate: new Date().toISOString()
        });
      }

      // Start 2-minute countdown (120 seconds) for analyze action
      const targetUrl = currentProject?.url || keywordData.targetUrl;
      const analyzeKey = getAnalyzeCountdownKey(targetUrl);
      if (analyzeKey) {
        const countdownEndTime = Date.now() + (2 * 60 * 1000);
        localStorage.setItem(analyzeKey, countdownEndTime.toString());
        setAnalyzeCountdown(120);
      }
    } catch (err) {
      console.error('Re-analyze keyword error:', err);
      let errorMessage = 'Failed to re-analyze keyword. Please try again.';
      
      if (err.response) {
        // Server responded with error status
        errorMessage = err.response.data?.error || `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Unable to connect to server. Please make sure the backend server is running on port 5001.';
      } else {
        // Error setting up the request
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setShowLoadingScreen(false);
    }
  };

  const handleDownloadReport = async () => {
    const selectedKeyword = keywords.find(k => k.id === selectedKeywordId);
    if (!selectedKeyword || !selectedKeyword.results || !reportRef.current) return;

    // Prevent download during countdown
    if (downloadCountdown > 0) return;
    
    // Show full-page loading screen
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setFunFact(randomFact);
    setLoadingMessage('Generating report...');
    setShowLoadingScreen(true);

    // eslint-disable-next-line no-unused-vars
    const results = selectedKeyword.results;
    
    // Force light theme for the printed report
    const prevThemeAttr = document.documentElement.getAttribute('data-theme') || 'light';
    if (prevThemeAttr !== 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    }

    // Wait a moment for theme to apply
    await new Promise(resolve => setTimeout(resolve, 100));

    // Load custom report settings from localStorage
    let reportSettings = {
      logoUrl: '/logo.png',
      leftFooterText: 'Generated by Parrot SEO',
      rightFooterText: 'parrotseo.com',
      watermarkEnabled: true
    };
    
    try {
      const savedSettings = localStorage.getItem('reportSettings');
      const normalizedPlan = (userPlan || 'basic').toLowerCase();
      const isBasicPlan = !normalizedPlan || normalizedPlan === 'basic';

      if (!isBasicPlan && savedSettings) {
        // Only apply custom branding for non-basic plans
        reportSettings = { ...reportSettings, ...JSON.parse(savedSettings) };
      } else if (isBasicPlan) {
        // Force defaults for Basic plan users to avoid using any old custom settings
        reportSettings = {
          logoUrl: '/logo.png',
          leftFooterText: 'Generated by Parrot SEO',
          rightFooterText: 'parrotseo.com',
          watermarkEnabled: true
        };
      }
    } catch (error) {
      console.warn('Error loading report settings:', error);
    }

    try {
      // Create new PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Set margins
      const margin = 15;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Helper to add footer to a specific page
      const addFooterToPage = (pageNumber) => {
        pdf.setPage(pageNumber);
        const footerY = pageHeight - margin + 5;
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.text(reportSettings.leftFooterText || 'Generated by Parrot SEO', margin, footerY);
        const rightText = reportSettings.rightFooterText || 'parrotseo.com';
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, footerY);
      };

      // Helper to add footer to current page
      const addFooter = () => {
        const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
        addFooterToPage(currentPage);
      };

      // Helper to add new page if needed
      const checkNewPage = (requiredHeight = 10) => {
        if (yPosition + requiredHeight > pageHeight - margin - 10) {
          addFooter();
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper function to split text into lines that fit page width
      const splitTextIntoLines = (pdf, text, maxWidth, fontSize) => {
        if (!text) return [];
        
        const lines = [];
        const words = text.split(' ');
        let currentLine = '';
        
        // Set font size for accurate width calculation
        pdf.setFontSize(fontSize);
        
        words.forEach(word => {
          // Handle very long words that exceed maxWidth
          const wordWidth = pdf.getTextWidth(word);
          if (wordWidth > maxWidth) {
            // If current line has content, save it first
            if (currentLine) {
              lines.push(currentLine);
              currentLine = '';
            }
            // Break long word into chunks
            let remainingWord = word;
            while (remainingWord.length > 0) {
              let chunk = '';
              for (let i = 0; i < remainingWord.length; i++) {
                const testChunk = chunk + remainingWord[i];
                if (pdf.getTextWidth(testChunk) <= maxWidth) {
                  chunk = testChunk;
                } else {
                  break;
                }
              }
              if (chunk) {
                lines.push(chunk);
                remainingWord = remainingWord.substring(chunk.length);
              } else {
                // Single character is too wide, add it anyway
                lines.push(remainingWord[0]);
                remainingWord = remainingWord.substring(1);
              }
            }
          } else {
            // Normal word processing
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const testWidth = pdf.getTextWidth(testLine);
            if (testWidth > maxWidth && currentLine) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          }
        });
        
        if (currentLine) {
          lines.push(currentLine);
        }
        
        return lines;
      };

      // Helper function to render robots.txt table without header (content only)
      const renderRobotsTableWithoutHeader = (pdf, rows, startX, startY, colWidths, margin, pageHeight, checkNewPageFn, rowHeight = 7) => {
        let yPos = startY;
        const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
        
        // Set font for data rows
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        
        // Draw data rows with page break handling
        rows.forEach((row, rowIndex) => {
          const cellText = row[0] || '-';
          
          // Split by newlines to preserve line breaks
          const contentLines = cellText.split('\n');
          
          // Calculate total height needed for all lines
          let totalLines = 0;
          contentLines.forEach(line => {
            const wrappedLines = splitTextIntoLines(pdf, line.trim(), colWidths[0] - 4, 8);
            totalLines += wrappedLines.length || 1; // At least 1 line even if empty
          });
          
          const cellHeight = Math.max(totalLines * 4 + 4, rowHeight);
          
          // Check if we need a new page
          if (yPos + cellHeight > pageHeight - margin) {
            pdf.addPage();
            yPos = margin;
          }
          
          // Draw row with unified border
          pdf.rect(startX, yPos, totalWidth, cellHeight);
          
          // Draw content line by line, preserving newlines
          let currentY = yPos + 5;
          contentLines.forEach((line, lineIndex) => {
            const trimmedLine = line.trim();
            if (trimmedLine || lineIndex === 0) { // Include empty lines or first line
              const wrappedLines = splitTextIntoLines(pdf, trimmedLine || ' ', colWidths[0] - 4, 8);
              wrappedLines.forEach((wrappedLine, wrappedIndex) => {
                pdf.text(wrappedLine || ' ', startX + 2, currentY, { 
                  maxWidth: colWidths[0] - 4,
                  align: 'left'
                });
                currentY += 4;
              });
            } else {
              // Empty line - add some spacing
              currentY += 4;
            }
          });
          
          yPos += cellHeight;
        });
        
        return yPos;
      };

      // Helper function to estimate robots.txt table height
      const estimateRobotsTableHeight = (content) => {
        if (!content) return 30;
        const lines = content.split('\n').length;
        const estimatedLines = Math.ceil(lines * 1.2);
        const estimatedHeight = Math.max(estimatedLines * 4 + 12, 30);
        return Math.min(estimatedHeight, 120);
      };

      // Helper to check space for title and table
      const checkSpaceForTitleAndTable = (estimatedTableHeight = 30) => {
        const titleHeight = 6;
        const totalRequired = titleHeight + estimatedTableHeight;
        const buffer = 10;
        if (yPosition + totalRequired + buffer > pageHeight - margin - 10) {
          addFooter();
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Helper to render table with proper text wrapping and cell sizing
      const renderTableWithCustomWidths = (pdf, headers, rows, startX, startY, colWidths, margin, pageHeight, checkNewPageFn, rowHeight = 7) => {
        let yPos = startY;
        const headerHeight = 8;
        const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);
        
        // Set font for headers
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        
        // Draw header row with unified border
        const drawHeader = (y) => {
          // Draw outer border for header
          pdf.rect(startX, y, totalWidth, headerHeight);
          
          let currentX = startX;
          headers.forEach((header, colIndex) => {
            const colWidth = colWidths[colIndex];
            // Draw vertical line between columns (except for first column)
            if (colIndex > 0) {
              pdf.line(currentX, y, currentX, y + headerHeight);
            }
            pdf.text(header, currentX + 2, y + 5, { maxWidth: colWidth - 4 });
            currentX += colWidth;
          });
        };
        
        drawHeader(yPos);
        yPos += headerHeight;
        
        // Set font for data rows
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        
        // Draw data rows with page break handling
        rows.forEach((row, rowIndex) => {
          // Calculate cell height based on all cells in the row
          // Add extra padding for remarks column (last column) and details column (index 1)
          const remarksPadding = 3; // Extra bottom padding for remarks
          const detailsPadding = 3; // Extra bottom padding for details column
          const cellHeight = Math.max(...row.map((cell, colIndex) => {
            const colWidth = colWidths[colIndex];
            const lines = splitTextIntoLines(pdf, cell || '', colWidth - 4, 8);
            const baseHeight = lines.length * 4 + 2;
            // Add extra padding for remarks column (last column) or details column (index 1)
            if (colIndex === row.length - 1) {
              return baseHeight + remarksPadding; // Last column (Remarks)
            } else if (colIndex === 1) {
              return baseHeight + detailsPadding; // Details column
            }
            return baseHeight;
          }), rowHeight);
          
          // Check if we need a new page
          if (yPos + cellHeight > pageHeight - margin) {
            pdf.addPage();
            yPos = margin;
            // Redraw header on new page
            drawHeader(yPos);
            yPos += headerHeight;
          }
          
          // Draw row with unified border
          pdf.rect(startX, yPos, totalWidth, cellHeight);
          
          // Draw row cells
          let currentX = startX;
          row.forEach((cell, colIndex) => {
            const colWidth = colWidths[colIndex];
            
            // Draw vertical line between columns (except for first column)
            if (colIndex > 0) {
              pdf.line(currentX, yPos, currentX, yPos + cellHeight);
            }
            
            const cellText = cell || '-';
            const cellMaxWidth = colWidth - 4; // Padding on both sides
            const lines = splitTextIntoLines(pdf, cellText, cellMaxWidth, 8);
            
            // Draw each line with proper spacing
            lines.forEach((line, lineIndex) => {
              const lineY = yPos + (lineIndex * 4) + 5;
              pdf.text(line, currentX + 2, lineY, { 
                maxWidth: cellMaxWidth,
                align: 'left'
              });
            });
            
            currentX += colWidth;
          });
          
          yPos += cellHeight; // No gap between rows - continuous table
        });
        
        return yPos;
      };

      // Add header with logo
      const headerStartY = yPosition;
      let logoHeight = 0;
      let logoWidth = 0;
      
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.src = reportSettings.logoUrl || '/logo.png';
        
        await new Promise((resolve) => {
          logoImg.onload = () => {
            try {
              logoHeight = 20;
              logoWidth = (logoImg.width / logoImg.height) * logoHeight;
              const logoX = pageWidth - margin - logoWidth;
              pdf.addImage(logoImg, 'PNG', logoX, headerStartY, logoWidth, logoHeight);
              resolve();
            } catch (error) {
              console.warn('Could not add logo to PDF:', error);
              resolve();
            }
          };
          logoImg.onerror = () => resolve();
          setTimeout(() => resolve(), 2000);
        });
      } catch (error) {
        console.warn('Error loading logo:', error);
      }

      // Add report title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Keyword Analysis Report', margin, headerStartY + 8, { maxWidth: maxWidth * 0.6 });
      
      // Add keyword and URL
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Keyword: ${selectedKeyword.keyword}`, margin, headerStartY + 16, { maxWidth: maxWidth * 0.6 });
      pdf.text(`Target URL: ${selectedKeyword.targetUrl}`, margin, headerStartY + 24, { maxWidth: maxWidth * 0.6 });
      
      yPosition = Math.max(headerStartY + logoHeight, headerStartY + 32) + 12;

      // Extract sections from DOM
      const sections = reportRef.current.querySelectorAll('.result-section');
      
      sections.forEach(section => {
        checkNewPage(15);
        
        const sectionTitle = section.querySelector('.section-title');
        let sectionTitleText = '';
        if (sectionTitle) {
          sectionTitleText = sectionTitle.textContent.trim();
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          const titleLines = splitTextIntoLines(pdf, sectionTitleText, maxWidth, 14);
          titleLines.forEach(line => {
            checkNewPage(9);
            pdf.text(line, margin, yPosition, { maxWidth: maxWidth });
            yPosition += 9;
          });
          yPosition += 2;
        }

        // Get detail rows and create table
        const detailRows = section.querySelectorAll('.detail-row');
        if (detailRows.length > 0) {
          const tableHeaders = ['Metric', 'Status', 'Value', 'Remarks'];
          const tableRows = [];
          
          detailRows.forEach(row => {
            const metricSection = row.querySelector('.row-metric-section .row-label');
            const statusSection = row.querySelector('.row-status-section .row-status-icon');
            const valueElement = row.querySelector('.row-remark-section .explanation-value');
            const remarkElement = row.querySelector('.row-remark-section .explanation-remark');
            
            const metric = metricSection ? metricSection.textContent.trim() : '';
            const status = statusSection ? (statusSection.classList.contains('status-good') ? 'Good' : 
                          statusSection.classList.contains('status-warning') ? 'Warning' : 'Error') : '';
            
            // Extract value - handle both text and React elements (like "Not available" span)
            let value = '';
            if (valueElement) {
              // Check if there's a muted span (for "Not available")
              const mutedElement = valueElement.querySelector('.muted');
              if (mutedElement) {
                value = mutedElement.textContent.trim();
              } else {
                // Get text content, handling nested elements
                value = valueElement.textContent.trim();
                // Remove any extra whitespace
                value = value.replace(/\s+/g, ' ').trim();
              }
            }
            
            // Extract remark - remove the " — " prefix if present
            let remark = '';
            if (remarkElement) {
              remark = remarkElement.textContent.trim();
              // Remove the " — " or " - " prefix if it exists
              if (remark.startsWith('—')) {
                remark = remark.substring(1).trim();
              } else if (remark.startsWith('-')) {
                remark = remark.substring(1).trim();
              }
            }
            
            // Skip additional content that will be shown in separate tables
            // (robots.txt, schemas, sitemaps, headings, images, and links are handled separately)
            const additionalContent = row.querySelector('.row-remark-section .additional-content');
            if (additionalContent) {
              // Check if this additional content contains robots.txt, schemas, sitemaps, headings, images, or links
              const hasCodeBlock = additionalContent.querySelector('.code-block');
              const hasSitemapLinks = additionalContent.querySelector('.row-link-stack');
              const hasSchemaTags = additionalContent.querySelector('.tag-container');
              const hasHeadingContainer = additionalContent.querySelector('.heading-container');
              const hasImageContainer = additionalContent.querySelector('.image-list-container');
              const hasLinkContainer = additionalContent.querySelector('.link-list-container');
              
              // Only add to remarks if it's not robots.txt, schemas, sitemaps, headings, images, or links
              if (!hasCodeBlock && !hasSitemapLinks && !hasSchemaTags && !hasHeadingContainer && !hasImageContainer && !hasLinkContainer) {
                const additionalText = additionalContent.textContent.trim();
                if (additionalText) {
                  remark = remark ? `${remark}\n${additionalText}` : additionalText;
                }
              }
            }
            
            // Also check for link-detail-row containers (links are shown outside additional-content)
            const linkDetailRow = row.nextElementSibling;
            if (linkDetailRow && linkDetailRow.classList.contains('link-detail-row')) {
              // Skip adding link content to remarks, it will be handled in separate tables
            }
            
            tableRows.push([metric, status, value, remark]);
          });
          
          // Use proportional column widths based on maxWidth
          // Metric: 20%, Status: 15%, Value: 25%, Remarks: 40%
          const colWidths = [maxWidth * 0.20, maxWidth * 0.15, maxWidth * 0.25, maxWidth * 0.40];
          yPosition = renderTableWithCustomWidths(pdf, tableHeaders, tableRows, margin, yPosition, colWidths, margin, pageHeight, checkNewPage);
          yPosition += 10; // Add spacing after table
        }

        // Handle robots.txt content in a separate table (only in Robots & Crawling Analysis section)
        const isRobotsSection = sectionTitleText === 'Robots & Crawling Analysis';
        const robotsContentElement = section.querySelector('.code-block');
        let robotsContent = '';
        if (robotsContentElement && isRobotsSection) {
          robotsContent = robotsContentElement.textContent.trim();
        }
        
        if (robotsContent && isRobotsSection) {
          // Estimate table height based on actual content
          const estimatedHeight = estimateRobotsTableHeight(robotsContent);
          checkSpaceForTitleAndTable(estimatedHeight); // Check space for title + table
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Detected robots.txt:', margin, yPosition);
          yPosition += 6;
          
          // Preserve newlines in robots.txt content
          const formattedRobotsContent = robotsContent;
          
          // Create a single-column table with robots.txt content (no header)
          const robotsRows = [[formattedRobotsContent]];
          
          if (robotsRows.length > 0) {
            const robotsColWidths = [maxWidth];
            // Use a custom rendering that preserves newlines
            yPosition = renderRobotsTableWithoutHeader(pdf, robotsRows, margin, yPosition, robotsColWidths, margin, pageHeight, checkNewPage);
            yPosition += 10;
          }
        }

        // Handle sitemap URLs table (all URLs in one cell, like robots.txt)
        const sitemapLinks = section.querySelectorAll('.row-link-stack a');
        if (sitemapLinks.length > 0) {
          checkSpaceForTitleAndTable(20); // Check space for title + table
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Detected Sitemaps:', margin, yPosition);
          yPosition += 6;
          
          // Combine all sitemap URLs into one string, separated by newlines
          const allSitemapUrls = Array.from(sitemapLinks)
            .map(link => link.textContent.trim() || link.href)
            .join('\n');
          
          // Create a single-column table with all sitemap URLs
          const sitemapRows = [[allSitemapUrls]];
          
          if (sitemapRows.length > 0) {
            const sitemapColWidths = [maxWidth];
            // Use a custom rendering that preserves newlines (no header)
            yPosition = renderRobotsTableWithoutHeader(pdf, sitemapRows, margin, yPosition, sitemapColWidths, margin, pageHeight, checkNewPage);
            yPosition += 10;
          }
        }

        // Handle schema types table (all schemas as tags in one cell)
        const schemaTags = section.querySelectorAll('.tag-container .schema-tag');
        if (schemaTags.length > 0) {
          checkSpaceForTitleAndTable(20); // Check space for title + table
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Detected Schema Types:', margin, yPosition);
          yPosition += 6;
          
          // Combine all schema tags into one string, separated by newlines
          const allSchemaTags = Array.from(schemaTags)
            .map(tag => tag.textContent.trim())
            .join('\n');
          
          // Create a single-column table with all schema tags
          const schemaRows = [[allSchemaTags]];
          
          if (schemaRows.length > 0) {
            const schemaColWidths = [maxWidth];
            // Use a custom rendering that preserves newlines (no header)
            yPosition = renderRobotsTableWithoutHeader(pdf, schemaRows, margin, yPosition, schemaColWidths, margin, pageHeight, checkNewPage);
            yPosition += 10;
          }
        }

        // Handle image tables - find parent container to determine issue type (only in Keyword-Image Analysis section)
        const isImageSection = sectionTitleText === 'Keyword-Image Analysis';
        if (isImageSection) {
          const imageContainers = section.querySelectorAll('.image-list-container');
          imageContainers.forEach(container => {
            checkNewPage(30);
            
            // Find the parent image-detail-row to get context
            const parentDetailRow = container.closest('.image-detail-row');
            
            // Determine title based on DetailRow label above or remark text
            let tableTitle = 'Detected Images';
            
            // First, try to find the DetailRow label above this container
            if (parentDetailRow) {
              let currentElement = parentDetailRow.previousElementSibling;
              while (currentElement) {
                const labelElement = currentElement.querySelector('.row-label');
                if (labelElement) {
                  const labelText = labelElement.textContent.trim();
                  if (labelText.includes('Images with Keyword') || labelText.includes('Keyword in Alt Text')) {
                    tableTitle = 'Detected Images with Keyword';
                    break;
                  } else if (labelText.includes('Missing Alt Text') || labelText.includes('Alt Text')) {
                    tableTitle = 'Detected Images with No Alt Text';
                    break;
                  } else if (labelText.includes('Large Images')) {
                    tableTitle = 'Detected Large Images (>300KB)';
                    break;
                  } else if (labelText.includes('WebP')) {
                    tableTitle = 'Detected Images Missing WebP Version';
                    break;
                  }
                  break;
                }
                currentElement = currentElement.previousElementSibling;
              }
              
              // If title not found from label, check remark text
              if (tableTitle === 'Detected Images') {
                const firstRemark = container.querySelector('.image-remark-text')?.textContent.trim() || '';
                
                if (firstRemark.includes('Add descriptive alt') || firstRemark.includes('alt text')) {
                  tableTitle = 'Detected Images with No Alt Text';
                } else if (firstRemark.includes('Compress') || firstRemark.includes('300KB')) {
                  tableTitle = 'Detected Large Images (>300KB)';
                } else if (firstRemark.includes('WebP') || firstRemark.includes('modern format')) {
                  tableTitle = 'Detected Images Missing WebP Version';
                } else if (firstRemark && firstRemark !== 'No alt text') {
                  // If there's a meaningful remark, it's likely images with keyword
                  tableTitle = 'Detected Images with Keyword';
                }
              }
              
              // Check if parent has image-detail-good class (images with keyword)
              if (parentDetailRow.classList.contains('image-detail-good')) {
                tableTitle = 'Detected Images with Keyword';
              }
            }
            
            const imageRows = container.querySelectorAll('.image-preview-row');
            if (imageRows.length > 0) {
              // Check space for title and table before adding title
              checkSpaceForTitleAndTable(30); // Check space for title + table
              // Add title before table
              pdf.setFontSize(10);
              pdf.setFont('helvetica', 'bold');
              pdf.text(tableTitle + ':', margin, yPosition);
              yPosition += 6;
              
              const headers = ['Image', 'Details', 'Remarks'];
              const rows = [];
              
              imageRows.forEach(row => {
                const imageCell = 'Image'; // Placeholder since we can't render images as text
                
                // Extract Details: only full image URL (no title, no cutoff)
                const detailsSection = row.querySelector('.image-preview-info');
                let detailsCell = '-';
                if (detailsSection) {
                  // Get the full URL from the anchor tag's href attribute, or from img src
                  const imageLinkAnchor = detailsSection.querySelector('.image-link');
                  if (imageLinkAnchor) {
                    // Get the full URL from href attribute (not truncated textContent)
                    detailsCell = imageLinkAnchor.getAttribute('href') || imageLinkAnchor.textContent.trim() || '-';
                  } else {
                    // Fallback: try to get from img element's src
                    const imgElement = row.querySelector('.image-thumbnail');
                    if (imgElement) {
                      detailsCell = imgElement.getAttribute('src') || imgElement.getAttribute('data-src') || '-';
                    } else {
                      // Last fallback: get all text but exclude title
                      const allText = detailsSection.textContent.trim();
                      const titleText = detailsSection.querySelector('.image-title')?.textContent.trim() || '';
                      if (titleText) {
                        detailsCell = allText.replace(titleText, '').trim() || '-';
                      } else {
                        detailsCell = allText || '-';
                      }
                    }
                  }
                }
                
                // Extract Remarks: alt text and title from remark section
                const remarksSection = row.querySelector('.image-preview-remark');
                let remarksCell = '-';
                if (remarksSection) {
                  // For "Detected Images with Keyword" table, format with labels
                  if (tableTitle === 'Detected Images with Keyword') {
                    const remarkLabels = remarksSection.querySelectorAll('.image-remark-label');
                    const remarkTexts = remarksSection.querySelectorAll('.image-remark-text');
                    const remarkParts = [];
                    
                    // Match labels with their corresponding text values
                    remarkLabels.forEach((labelEl, idx) => {
                      const labelText = labelEl.textContent.trim();
                      const textEl = remarkTexts[idx];
                      if (textEl) {
                        const text = textEl.textContent.trim();
                        if (text && text !== 'No alt text') {
                          // Format: "Detected Alt Text: [value]" or "Detected Title: [value]"
                          const formattedLabel = labelText === 'Alt Text' ? 'Detected Alt Text' : 
                                                 labelText === 'Title' ? 'Detected Title' : 
                                                 `Detected ${labelText}`;
                          remarkParts.push(`${formattedLabel}: ${text}`);
                        }
                      }
                    });
                    
                    // If no labels found, try to get text values directly
                    if (remarkParts.length === 0) {
                      remarkTexts.forEach(textEl => {
                        const text = textEl.textContent.trim();
                        if (text && text !== 'No alt text') {
                          remarkParts.push(`Detected Alt Text: ${text}`);
                        }
                      });
                    }
                    
                    if (remarkParts.length > 0) {
                      remarksCell = remarkParts.join('; ');
                    }
                  } else {
                    // For other image tables, use original logic
                    const remarkTexts = remarksSection.querySelectorAll('.image-remark-text');
                    const remarkParts = [];
                    remarkTexts.forEach(textEl => {
                      const text = textEl.textContent.trim();
                      if (text && text !== 'No alt text') {
                        remarkParts.push(text);
                      }
                    });
                    if (remarkParts.length > 0) {
                      remarksCell = remarkParts.join('\n');
                    } else {
                      // Fallback: get all text but exclude labels
                      const allText = remarksSection.textContent.trim();
                      const labelText = remarksSection.querySelector('.image-remark-label')?.textContent.trim() || '';
                      if (allText && allText !== labelText) {
                        remarksCell = allText.replace(labelText, '').trim() || '-';
                      }
                    }
                  }
                }
                
                rows.push([imageCell, detailsCell, remarksCell]);
              });
              
              if (rows.length > 0) {
                // Use custom column widths: Image (15%), Details (40%), Remarks (45%)
                const colWidths = [maxWidth * 0.15, maxWidth * 0.40, maxWidth * 0.45];
                yPosition = renderTableWithCustomWidths(pdf, headers, rows, margin, yPosition, colWidths, margin, pageHeight, checkNewPage);
                yPosition += 10; // Add spacing after table
              }
            }
          });
        }

        // Handle link tables - find parent container to determine link type (only in Link Profile Analysis section)
        const isLinkSection = sectionTitleText === 'Link Profile Analysis';
        if (isLinkSection) {
          const linkContainers = section.querySelectorAll('.link-list-container');
          linkContainers.forEach(container => {
            checkNewPage(30);
            
            // Find the parent link-detail-row to get context
            const parentDetailRow = container.closest('.link-detail-row');
            
            // Determine title based on DetailRow label above
            let tableTitle = 'Detected Links';
            
            if (parentDetailRow) {
              // Try to find the DetailRow label above this container
              let currentElement = parentDetailRow.previousElementSibling;
              while (currentElement) {
                const labelElement = currentElement.querySelector('.row-label');
                if (labelElement) {
                  const labelText = labelElement.textContent.trim();
                  if (labelText.includes('Internal Links')) {
                    tableTitle = 'Detected Internal Links';
                    break;
                  } else if (labelText.includes('External Links')) {
                    tableTitle = 'Detected External Links';
                    break;
                  } else if (labelText.includes('NoFollow Links') || labelText.includes('Nofollow Links')) {
                    tableTitle = 'Detected NoFollow Links';
                    break;
                  } else if (labelText.includes('Broken Links')) {
                    tableTitle = 'Detected Broken Links';
                    break;
                  }
                  break;
                }
                currentElement = currentElement.previousElementSibling;
              }
            }
            
            const linkRows = container.querySelectorAll('.link-preview-row');
            if (linkRows.length > 0) {
              // Check space for title and table before adding title
              checkSpaceForTitleAndTable(30); // Check space for title + table
              // Add title before table
              pdf.setFontSize(10);
              pdf.setFont('helvetica', 'bold');
              pdf.text(tableTitle + ':', margin, yPosition);
              yPosition += 6;
              
              const headers = ['Link Type', 'Anchor Text', 'URL'];
              const rows = [];
              
              linkRows.forEach(row => {
                const typeCell = row.querySelector('.link-preview-type .link-type-tag')?.textContent.trim() || 
                                row.querySelector('.link-preview-type')?.textContent.trim() || '-';
                const anchorCell = row.querySelector('.link-preview-anchor .link-anchor')?.textContent.trim() || 
                                 row.querySelector('.link-preview-anchor .link-image-anchor span')?.textContent.trim() || 
                                 (row.querySelector('.link-preview-anchor .link-image-anchor img') ? 'Image Link' : '-');
                
                // Extract URL from href attribute for full URL (no cutoff)
                let urlCell = '-';
                const urlAnchor = row.querySelector('.link-preview-url .link-url');
                if (urlAnchor) {
                  urlCell = urlAnchor.getAttribute('href') || urlAnchor.textContent.trim() || '-';
                } else {
                  urlCell = row.querySelector('.link-preview-url')?.textContent.trim() || '-';
                }
                
                rows.push([typeCell, anchorCell, urlCell]);
              });
              
              if (rows.length > 0) {
                // Use custom column widths: Link Type (20%), Anchor Text (35%), URL (45%)
                const colWidths = [maxWidth * 0.20, maxWidth * 0.35, maxWidth * 0.45];
                yPosition = renderTableWithCustomWidths(pdf, headers, rows, margin, yPosition, colWidths, margin, pageHeight, checkNewPage);
                yPosition += 10; // Add spacing after table
              }
            }
          });
        }

        // Handle headings - group by type and create separate tables (only in Keyword-Heading Analysis section)
        const isHeadingSection = sectionTitleText === 'Keyword-Heading Analysis';
        if (isHeadingSection) {
          // Find all heading containers with their labels
          const headingGroups = [];
          const allAdditionalContent = section.querySelectorAll('.additional-content');
          
          allAdditionalContent.forEach(additionalContent => {
            const labelElement = additionalContent.querySelector('.additional-content-label');
            const headingContainer = additionalContent.querySelector('.heading-container');
            
            if (labelElement && headingContainer) {
              const labelText = labelElement.textContent.trim();
              // Extract heading type from label (e.g., "Detected H1 Headings:" -> "H1")
              const headingTypeMatch = labelText.match(/Detected\s+(H[1-6])\s+Headings?:/i);
              if (headingTypeMatch) {
                const headingType = headingTypeMatch[1];
                const headingBoxes = headingContainer.querySelectorAll('.heading-box');
                const headings = Array.from(headingBoxes)
                  .map(box => box.textContent.trim())
                  .filter(text => text.length > 0);
                
                if (headings.length > 0) {
                  headingGroups.push({ type: headingType, headings });
                }
              }
            }
          });
          
          // Create separate table for each heading type
          headingGroups.forEach(group => {
            checkSpaceForTitleAndTable(20); // Check space for title + table
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Detected ${group.type} Headings:`, margin, yPosition);
            yPosition += 6;
            
            // Create table with columns: Heading Type and Detected Heading
            const tableHeaders = ['Heading Type', 'Detected Heading'];
            const tableRows = group.headings.map(heading => [group.type, heading]);
            
            if (tableRows.length > 0) {
              const colWidths = [maxWidth * 0.2, maxWidth * 0.8];
              yPosition = renderTableWithCustomWidths(pdf, tableHeaders, tableRows, margin, yPosition, colWidths, margin, pageHeight, checkNewPage);
              yPosition += 10; // Add spacing after table
            }
          });
        }
      });

      // Add footer to all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        addFooterToPage(i);
        
        // Add watermark if enabled
        if (reportSettings.watermarkEnabled) {
          try {
            const logoImg = new Image();
            logoImg.crossOrigin = 'anonymous';
            logoImg.src = reportSettings.logoUrl || '/logo.png';
            
            await new Promise((resolve) => {
              logoImg.onload = () => {
                try {
                  const canvas = document.createElement('canvas');
                  canvas.width = logoImg.width;
                  canvas.height = logoImg.height;
                  const ctx = canvas.getContext('2d');
                  ctx.globalAlpha = 0.1;
                  ctx.drawImage(logoImg, 0, 0);
                  const watermarkData = canvas.toDataURL('image/png');
                  
                  const watermarkWidth = 60;
                  const watermarkHeight = (logoImg.height / logoImg.width) * watermarkWidth;
                  const centerX = (pageWidth - watermarkWidth) / 2;
                  const centerY = (pageHeight - watermarkHeight) / 2;
                  
                  pdf.addImage(watermarkData, 'PNG', centerX, centerY, watermarkWidth, watermarkHeight);
                  resolve();
                } catch (error) {
                  resolve();
                }
              };
              logoImg.onerror = () => resolve();
              setTimeout(() => resolve(), 1000);
            });
          } catch (error) {
            console.warn('Error adding watermark:', error);
          }
        }
      }

      // Restore theme
      if (prevThemeAttr !== 'light') {
        document.documentElement.setAttribute('data-theme', prevThemeAttr);
      }

      // Save PDF
      const fileName = `Keyword-Analysis-${selectedKeyword.keyword.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      // Start 2-minute countdown (120 seconds) after successful download
      const targetUrl = currentProject?.url || selectedKeyword.targetUrl;
      const downloadKey = getDownloadCountdownKey(targetUrl);
      if (downloadKey) {
        const countdownEndTime = Date.now() + (2 * 60 * 1000);
        localStorage.setItem(downloadKey, countdownEndTime.toString());
        setDownloadCountdown(120);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate report. Please try again.');
      
      // Restore theme
      if (prevThemeAttr !== 'light') {
        document.documentElement.setAttribute('data-theme', prevThemeAttr);
      }
    } finally {
      setShowLoadingScreen(false);
    }
  };

  const handleDeleteKeyword = (keywordId) => {
    const keywordData = keywords.find(k => k.id === keywordId);
    if (!keywordData) return;

    setDeletingKeywordId(keywordId);
    setDeletingKeywordName(keywordData.keyword);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (!deletingKeywordId) return;

    // Store the deleted keyword data before removing it
    const deletedKeyword = keywords.find(k => k.id === deletingKeywordId);
    if (deletedKeyword) {
      setRecentlyDeletedKeyword(deletedKeyword);
      
      // Persist to Supabase (cross-device support)
      const saveToSupabase = async () => {
        if (!authUser || !currentProject) return;
        
        try {
          const { data: { user: supabaseUser } } = await supabase.auth.getUser();
          if (!supabaseUser) return;

          // Delete any existing recently deleted keywords for this project
          await supabase
            .from('recently_deleted_keywords')
            .delete()
            .eq('user_id', supabaseUser.id)
            .eq('project_id', currentProject.id);

          // Insert the new recently deleted keyword
          const { error } = await supabase
            .from('recently_deleted_keywords')
            .insert({
              user_id: supabaseUser.id,
              project_id: currentProject.id,
              keyword_data: deletedKeyword
            });

          if (error) {
            console.error('Error saving recently deleted keyword to Supabase:', error);
          }
        } catch (err) {
          console.error('Error saving recently deleted keyword to Supabase:', err);
        }
      };

      saveToSupabase();

      // Also persist to localStorage as fallback
      if (currentProject && currentProject.url) {
        const recentlyDeletedKey = getRecentlyDeletedKey(currentProject.url);
        if (recentlyDeletedKey) {
          try {
            localStorage.setItem(recentlyDeletedKey, JSON.stringify(deletedKeyword));
          } catch (err) {
            console.error('Error saving recently deleted keyword to localStorage:', err);
          }
        }
      }
    }

    const updatedKeywords = keywords.filter(k => k.id !== deletingKeywordId);
    setKeywords(updatedKeywords);
    
    if (selectedKeywordId === deletingKeywordId) {
      setSelectedKeywordId(updatedKeywords.length > 0 ? updatedKeywords[0].id : null);
    }

    // Save to localStorage cache
    if (currentProject && currentProject.url) {
      const cacheKey = getCacheKey(currentProject.url);
      if (cacheKey) {
        try {
          const cacheData = {
            data: updatedKeywords,
            cachedAt: new Date().toISOString(),
            timestamp: new Date().toISOString()
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (err) {
          console.error('Error saving keyword analyses to cache:', err);
        }
      }
    }

    // Save to project
    if (currentProject) {
      updateProject(currentProject.id, {
        keywordAnalyses: updatedKeywords
      });
    }

    // Close confirmation dialog
    setShowDeleteConfirm(false);
    setDeletingKeywordId(null);
    setDeletingKeywordName('');
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletingKeywordId(null);
    setDeletingKeywordName('');
  };

  const handleRestoreDeletedKeyword = () => {
    if (!recentlyDeletedKeyword) return;

    // Restore the keyword
    const restoredKeywords = [...keywords, recentlyDeletedKeyword];
    setKeywords(restoredKeywords);
    setSelectedKeywordId(recentlyDeletedKeyword.id);
    setRecentlyDeletedKeyword(null);
    // Clear from Supabase
    const clearFromSupabase = async () => {
      if (!authUser || !currentProject) return;
      
      try {
        const { data: { user: supabaseUser } } = await supabase.auth.getUser();
        if (!supabaseUser) return;

        await supabase
          .from('recently_deleted_keywords')
          .delete()
          .eq('user_id', supabaseUser.id)
          .eq('project_id', currentProject.id);
      } catch (err) {
        console.error('Error clearing recently deleted keyword from Supabase:', err);
      }
    };
    clearFromSupabase();
    // Clear from localStorage
    if (currentProject && currentProject.url) {
      const recentlyDeletedKey = getRecentlyDeletedKey(currentProject.url);
      if (recentlyDeletedKey) {
        try {
          localStorage.removeItem(recentlyDeletedKey);
        } catch (err) {
          console.error('Error clearing recently deleted keyword from localStorage:', err);
        }
      }
    }

    // Save to localStorage cache
    if (currentProject && currentProject.url) {
      const cacheKey = getCacheKey(currentProject.url);
      if (cacheKey) {
        try {
          const cacheData = {
            data: restoredKeywords,
            cachedAt: new Date().toISOString(),
            timestamp: new Date().toISOString()
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (err) {
          console.error('Error saving keyword analyses to cache:', err);
        }
      }
    }

    // Save to project
    if (currentProject) {
      updateProject(currentProject.id, {
        keywordAnalyses: restoredKeywords
      });
    }
  };

  // Helper function to check if keyword appears in headings
  const checkKeywordInHeadings = (headings, keywordLower) => {
    if (!headings) return { found: false, locations: [] };
    
    const locations = [];
    const allHeadings = [
      ...(headings.h1 || []),
      ...(headings.h2 || []),
      ...(headings.h3 || []),
      ...(headings.h4 || []),
      ...(headings.h5 || []),
      ...(headings.h6 || [])
    ];

    allHeadings.forEach((heading, index) => {
      if (heading.toLowerCase().includes(keywordLower)) {
        locations.push({
          text: heading,
          level: index < (headings.h1?.length || 0) ? 'H1' :
               index < ((headings.h1?.length || 0) + (headings.h2?.length || 0)) ? 'H2' : 'H3+'
        });
      }
    });

    return {
      found: locations.length > 0,
      locations: locations
    };
  };

  // Helper function to check if keyword appears in content
  const checkKeywordInContent = (content, keywordLower) => {
    if (!content || !content.textContent) return false;
    return content.textContent.toLowerCase().includes(keywordLower);
  };

  // Helper function to calculate keyword density
  const calculateKeywordDensity = (content, keywordLower) => {
    if (!content || !content.textContent || content.wordCount === 0) return 0;
    
    const text = content.textContent.toLowerCase();
    const keywordRegex = new RegExp(`\\b${keywordLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    const matches = text.match(keywordRegex);
    const keywordCount = matches ? matches.length : 0;
    
    return ((keywordCount / content.wordCount) * 100).toFixed(2);
  };

  // Helper function to count keyword occurrences
  const countKeywordOccurrences = (content, keywordLower) => {
    if (!content || !content.textContent) return 0;
    
    const text = content.textContent.toLowerCase();
    const keywordRegex = new RegExp(`\\b${keywordLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    const matches = text.match(keywordRegex);
    return matches ? matches.length : 0;
  };

  // Helper function to find keyword positions in content
  const findKeywordPositions = (content, keywordLower) => {
    if (!content || !content.textContent) return [];
    
    const text = content.textContent.toLowerCase();
    const keywordRegex = new RegExp(`\\b${keywordLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    const positions = [];
    let match;
    
    while ((match = keywordRegex.exec(text)) !== null) {
      positions.push({
        position: match.index,
        context: getContextAroundPosition(content.textContent, match.index, 50)
      });
    }
    
    return positions.slice(0, 10); // Limit to first 10 occurrences
  };

  // Helper function to get context around a keyword position
  const getContextAroundPosition = (text, position, contextLength) => {
    const start = Math.max(0, position - contextLength);
    const end = Math.min(text.length, position + contextLength);
    let context = text.substring(start, end);
    
    if (start > 0) context = '...' + context;
    if (end < text.length) context = context + '...';
    
    return context;
  };

  return (
    <div className="keyword-analysis-page seo-analyzer">
      {/* Full-page loading overlay similar to Dashboard/Site Audit */}
      {showLoadingScreen && (
        <div className="page-loader-overlay">
          <div className="page-loader-content">
            {funFact && (
              <div className="fun-fact-container">
                <div className="fun-fact-icon">💡</div>
                <p className="fun-fact-text">{funFact}</p>
              </div>
            )}
            <div className="page-loader-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <p className="page-loader-text">{loadingMessage}</p>
            <p className="page-loader-message">
              This will take a few seconds, depends on the website size. Kindly wait until our algorithms analyze your website.
            </p>
          </div>
        </div>
      )}
      <header className="header">
        <div className="header-container">
          <div className="header-actions">
            <a href="/pricing" className="header-menu-item">
              Plans & Pricing
            </a>
            {user && (
              <div className="user-info">
                <button
                  className="user-icon-button"
                  onClick={() => {
                    window.location.href = '/dashboard/account-settings';
                  }}
                  aria-label="Account settings"
                  title="Account settings"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="container" style={{ position: 'relative' }}>
        {/* Show loading screen in content area only when credits are loading AND no keywords exist */}
        {userPlan === 'basic' && creditsLoading && keywords.length === 0 ? (
          <div className="content-loader-overlay">
            <div className="page-loader-content">
              <div className="page-loader-spinner">
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
                <div className="spinner-ring"></div>
              </div>
              <p className="page-loader-text">Loading...</p>
              <p className="page-loader-message">
                Checking your keyword analysis credits...
              </p>
            </div>
          </div>
        ) : null}
        {/* Show pro feature box if credits exhausted and no keywords (only check when credits loaded) */}
        {(!creditsLoading || keywords.length > 0) && (
          <>
          {userPlan === 'basic' && !creditsLoading && keywordCredits.creditsRemaining === 0 && keywords.length === 0 ? (
          <div className="keyword-pro-lock">
            <div className="keyword-pro-lock-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <div className="keyword-pro-lock-content">
              <h2 className="keyword-pro-lock-title">Keyword Analysis is a Pro feature</h2>
              <p className="keyword-pro-lock-text">
                You've used all 3 keyword analyses available on the Basic plan. 
                Upgrade your plan to unlock unlimited keyword analysis and advanced SEO insights.
              </p>
              <div className="keyword-pro-lock-actions">
                {recentlyDeletedKeyword && (
                  <button
                    type="button"
                    className="keyword-pro-lock-restore-btn"
                    onClick={handleRestoreDeletedKeyword}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                      <path d="M21 3v5h-5"></path>
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                      <path d="M3 21v-5h5"></path>
                    </svg>
                    Use "{recentlyDeletedKeyword.keyword}"
                  </button>
                )}
                <button
                  type="button"
                  className="keyword-pro-lock-button"
                  onClick={() => window.open('https://parrotseo.com/pricing', '_blank', 'noopener,noreferrer')}
                >
                  Upgrade Plan
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="keyword-analysis-header">
              <h1 className="keyword-analysis-title">Keyword Analysis</h1>
              <button
                type="button"
                className="add-keyword-btn"
                onClick={handleOpenAddModal}
                disabled={loading}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Keyword
              </button>
            </div>
            <div className="keywords-section">
            <div className="keywords-box">
              {keywords.length === 0 ? (
                <div className="no-keywords-message">
                  <p>No keywords added yet. Click "Add Keyword" to get started.</p>
                </div>
              ) : (
                <div className="keywords-tags">
                  {keywords.map((kw) => (
                    <div
                      key={kw.id}
                      className={`keyword-tag ${selectedKeywordId === kw.id ? 'active' : ''}`}
                      onClick={() => setSelectedKeywordId(kw.id)}
                    >
                      <span className="keyword-tag-text">{kw.keyword}</span>
                      <button
                        className="keyword-tag-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteKeyword(kw.id);
                        }}
                        title="Delete keyword"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </>
        )}
          </>
        )}

        {selectedKeywordId && (() => {
          const selectedKeyword = keywords.find(k => k.id === selectedKeywordId);
          const results = selectedKeyword?.results;
          if (!results) return null;

          return (
            <div className="keyword-results-section">
              <div className="keyword-results-header">
                <div className="selected-keyword-info">
                  <div className="selected-keyword-row">
                    <span className="selected-keyword-label">Keyword:</span>
                    <h3 className="selected-keyword-title">{selectedKeyword.keyword}</h3>
                    <div className="keyword-actions">
                      <button
                        type="button"
                        className="delete-keyword-btn"
                        onClick={() => handleDeleteKeyword(selectedKeywordId)}
                        title="Delete keyword"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="selected-keyword-row">
                    <span className="selected-keyword-label">Target URL:</span>
                    <span className="selected-keyword-url">{selectedKeyword.targetUrl}</span>
                  </div>
                </div>
                <div className="keyword-results-actions">
                  <div className="last-analysis-info">
                    <div className="last-analysis-label">Last Analysis:</div>
                    <div className="last-analysis-date">
                      {new Date(selectedKeyword.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="re-analyze-btn"
                    onClick={() => handleReAnalyze(selectedKeywordId)}
                    disabled={loading || analyzeCountdown > 0}
                  >
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        Analyzing...
                      </>
                    ) : analyzeCountdown > 0 ? (
                      `RE-ANALYZE (${Math.floor(analyzeCountdown / 60)}:${String(analyzeCountdown % 60).padStart(2, '0')})`
                    ) : (
                      'RE-ANALYZE'
                    )}
                  </button>
                  <button
                    type="button"
                    className="download-report-btn"
                    onClick={handleDownloadReport}
                    title="Download Report"
                    disabled={downloadCountdown > 0}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>
                      {downloadCountdown > 0 
                        ? `Download Report (${Math.floor(downloadCountdown / 60)}:${String(downloadCountdown % 60).padStart(2, '0')})`
                        : 'Download Report'}
                    </span>
                  </button>
                </div>
              </div>
              <div className="results-container" ref={reportRef}>
            {/* Keyword-Meta Analysis Section - First */}
            {results.metaTitle && (
              <section className="result-section">
                <div className="section-header">
                  <h3 className="section-title">Keyword-Meta Analysis</h3>
                  <CircularProgress percentage={calculateMetaScore(results)} />
                </div>
                <div className="section-content">
                  {(() => {
                    const titleFeedback = getLengthFeedback(results.metaTitle, MIN_TITLE, MAX_TITLE, 'Meta title');
                    const titleKeywordRemark = results.inTitle
                      ? 'Your keyword is present in the meta title. This is excellent for SEO. '
                      : 'Consider adding your target keyword to the meta title for better search visibility. ';
                    const titleCombinedRemark = titleKeywordRemark + titleFeedback.remark;
                    // Status: good if both keyword and length are good, otherwise use the worse status
                    const titleStatus = (results.inTitle && titleFeedback.status === 'good') 
                      ? 'good' 
                      : (titleFeedback.status === 'error' ? 'error' : 'warning');
                    
                    const descFeedback = getLengthFeedback(
                      results.metaDescription,
                      MIN_DESCRIPTION,
                      MAX_DESCRIPTION,
                      'Meta description'
                    );
                    const descKeywordRemark = results.inDescription
                      ? 'Your keyword is present in the meta description, which can help improve click-through rates. '
                      : 'Consider adding your target keyword to the meta description naturally. ';
                    const descCombinedRemark = descKeywordRemark + descFeedback.remark;
                    // Status: good if both keyword and length are good, otherwise use the worse status
                    const descStatus = (results.inDescription && descFeedback.status === 'good') 
                      ? 'good' 
                      : (descFeedback.status === 'error' ? 'error' : 'warning');
                    
                    return (
                      <>
                        <DetailRow
                          label="Meta Title"
                          value={results.metaTitle}
                          remark={titleCombinedRemark}
                          status={titleStatus}
                        />
                        {results.metaDescription && (
                          <DetailRow
                            label="Meta Description"
                            value={results.metaDescription}
                            remark={descCombinedRemark}
                            status={descStatus}
                          />
                        )}
                      </>
                    );
                  })()}
                  <DetailRow
                    label="Meta Robots"
                    value={results.metaRobots || 'Not specified'}
                    remark={
                      results.metaRobots?.toLowerCase().includes('noindex')
                        ? 'Your page is set to "noindex", which tells search engines not to show this page in search results. Only use this if you intentionally want to hide the page from search. If you want this page to appear in search results, remove the noindex directive.'
                        : 'Your page allows search engines to index it, which means it can appear in search results. This is usually what you want unless you have a specific reason to hide the page.'}
                    status={results.metaRobots?.toLowerCase().includes('noindex') ? 'error' : 'good'}
                  />
                  <DetailRow
                    label="Indexing Status"
                    value={results.indexingStatus || 'unknown'}
                    remark={
                      results.indexingStatus === 'index'
                        ? 'Your page is set to be indexed by search engines, which means it can appear in search results. Monitor your page\'s performance in Google Search Console to see how it\'s performing.'
                        : 'Your page is set to "noindex", which prevents it from appearing in search results. If you want people to find this page through search, you need to remove the noindex setting.'}
                    status={results.indexingStatus === 'index' ? 'good' : 'warning'}
                  />
                  {(() => {
                    const ogImage = results.auditResults?.meta?.ogImage;
                    const ogDimensions = results.auditResults?.meta?.ogImageDimensions;
                    // Ensure we're comparing numbers, not strings
                    const width = ogDimensions ? Number(ogDimensions.width) : null;
                    const height = ogDimensions ? Number(ogDimensions.height) : null;
                    const hasDimensions = width !== null && height !== null && !isNaN(width) && !isNaN(height);
                    const isOptimalSize = hasDimensions && width === 1200 && height === 630;
                    
                    let ogRemark = '';
                    let ogStatus = 'warning'; // Default to warning
                    
                    if (!ogImage) {
                      ogRemark = 'Your page doesn\'t have an Open Graph image set. Adding one will make your links look better when shared on social media platforms like Facebook, Twitter, and LinkedIn. For the best appearance, use an image that\'s 1200×630 pixels.';
                      ogStatus = 'warning';
                    } else if (!hasDimensions) {
                      ogRemark = 'Your page has an Open Graph image set. This image appears when your page is shared on social media platforms like Facebook, Twitter, and LinkedIn, making your links more attractive and clickable. For the best appearance, use an image that\'s 1200×630 pixels.';
                      ogStatus = 'warning'; // Warning since we can't verify the size
                    } else if (isOptimalSize) {
                      ogRemark = 'Your page has an Open Graph image set with optimal dimensions (1200×630 pixels). This image appears when your page is shared on social media platforms like Facebook, Twitter, and LinkedIn, making your links more attractive and clickable.';
                      ogStatus = 'good';
                    } else {
                      // Has image and dimensions, but not optimal size
                      ogRemark = `Your page has an Open Graph image set, but it's not the recommended size. Your image is ${width}×${height} pixels, but for the best appearance on social media platforms like Facebook, Twitter, and LinkedIn, use an image that's exactly 1200×630 pixels.`;
                      ogStatus = 'warning';
                    }
                    
                    return (
                      <DetailRow
                        label="Open Graph Image"
                        value={
                          ogImage
                            ? (
                              <div className="og-image-inline">
                                <img 
                                  src={ogImage} 
                                  alt="OG preview" 
                                  onError={(e) => (e.target.style.display = 'none')} 
                                />
                                <a href={ogImage} target="_blank" rel="noopener noreferrer">
                                  {ogImage}
                                </a>
                                {ogDimensions && width !== null && height !== null && (
                                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>
                                    Dimensions: {width} × {height} pixels
                                    {!isOptimalSize && (
                                      <span style={{ color: 'var(--warning-color)', marginLeft: '8px' }}>
                                        (Recommended: 1200×630 pixels)
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                            : 'Not set'
                        }
                        remark={ogRemark}
                        status={ogStatus}
                      />
                    );
                  })()}
                </div>
              </section>
            )}

            {/* Security & Redirection Analysis Section */}
            {(() => {
              const security = results.auditResults?.security;
              if (!security) return null;
              return (
                <section className="result-section">
                  <div className="section-header">
                    <h3 className="section-title">Security & Redirection Analysis</h3>
                    <CircularProgress percentage={calculateSecurityScore(security)} />
                  </div>
                  <div className="section-content">
                    <DetailRow
                      label="SSL Certificate"
                      value={security.sslCertificate ? 'Valid certificate detected' : 'Not detected'}
                      remark={
                        security.sslCertificate
                          ? 'Your website uses HTTPS, which protects visitor data.'
                          : 'Your website is missing SSL. Install one to encrypt data and improve trust.'
                      }
                      status={security.sslCertificate ? 'good' : 'warning'}
                    />
                    <DetailRow
                      label="HTTPS Handling"
                      value={security.httpsRedirection ? 'HTTPS enforced' : 'HTTP allowed'}
                      remark={
                        security.httpsRedirection
                          ? 'HTTP traffic is automatically redirected to HTTPS.'
                          : 'Ensure HTTP traffic is redirected to HTTPS to avoid duplicate content and improve security.'
                      }
                      status={security.httpsRedirection ? 'good' : 'warning'}
                    />
                    <DetailRow
                      label="WWW Redirection"
                      value={security.wwwRedirection?.type || 'No redirection set'}
                      remark={
                        security.wwwRedirection?.redirected
                          ? 'Your website properly redirects between www and non-www versions (like example.com and www.example.com). This is good—just make sure all your internal links use the same version consistently.'
                          : 'This is a critical SEO issue! Your website doesn\'t redirect between www and non-www versions. Search engines may see your site as two separate websites, which can cause duplicate content problems and split your SEO rankings. You must choose one version (either with www or without www) and set up a permanent redirect from the other version immediately.'
                      }
                      status={security.wwwRedirection?.redirected ? 'good' : 'error'}
                    />
                  </div>
                </section>
              );
            })()}

            {/* Robots & Crawling Analysis Section */}
            {(() => {
              const robots = results.auditResults?.robots;
              if (!robots) return null;
              const { remark, status } = getRobotsRemark(robots);
              return (
                <section className="result-section">
                  <div className="section-header">
                    <h3 className="section-title">Robots & Crawling Analysis</h3>
                    <CircularProgress percentage={calculateRobotsScore(robots)} />
                  </div>
                  <div className="section-content">
                    <DetailRow
                      label="robots.txt"
                      value={robots.exists ? 'Found' : 'Not found'}
                      remark={remark}
                      status={status}
                      additionalContent={
                        robots.exists && robots.content ? (
                          <div>
                            <div className="additional-content-label">robots.txt Content</div>
                            <pre className="code-block">
                              {robots.content.substring(0, 500)}
                              {robots.content.length > 500 ? '...' : ''}
                            </pre>
                          </div>
                        ) : null
                      }
                    />
                    {robots.exists && (
                      <DetailRow
                        label="Accessibility"
                        value={robots.accessible ? 'Reachable (HTTP 200)' : 'Blocked or 4xx'}
                        remark={
                          robots.accessible
                            ? 'Search engines can read your robots.txt file.'
                            : "Search engines can't access your robots.txt file. Ensure it returns HTTP 200."
                        }
                        status={robots.accessible ? 'good' : 'warning'}
                      />
                    )}
                  </div>
                </section>
              );
            })()}

            {/* Structured Data & Sitemaps Analysis Section */}
            {(() => {
              const schema = results.auditResults?.schema;
              const sitemaps = results.auditResults?.sitemaps;
              if (!schema && !sitemaps) return null;
              const effectiveSchema = schema || { found: false, types: [] };
              const effectiveSitemaps = sitemaps || { found: false, sitemapUrls: [] };
              const schemaSummary = getSchemaRemark(effectiveSchema);
              const sitemapSummary = getSitemapRemark(effectiveSitemaps);
              return (
                <section className="result-section">
                  <div className="section-header">
                    <h3 className="section-title">Structured Data & Sitemaps Analysis</h3>
                    <CircularProgress
                      percentage={calculateSchemaSitemapScore(effectiveSchema, effectiveSitemaps)}
                    />
                  </div>
                  <div className="section-content">
                    <DetailRow
                      label="Schema Markup"
                      value={effectiveSchema.found ? 'Detected' : 'Not detected'}
                      remark={schemaSummary.remark}
                      status={schemaSummary.status}
                      additionalContent={
                        effectiveSchema.found && effectiveSchema.types?.length > 0 ? (
                          <div>
                            <div className="additional-content-label">Detected Schema Types:</div>
                            {formatTags(effectiveSchema.types)}
                          </div>
                        ) : null
                      }
                    />
                    <DetailRow
                      label="Sitemaps"
                      value={effectiveSitemaps.found ? 'Declared' : 'Not declared'}
                      remark={sitemapSummary.remark}
                      status={sitemapSummary.status}
                      additionalContent={
                        effectiveSitemaps.sitemapUrls?.length > 0 ? (
                          <div>
                            <div className="additional-content-label">Sitemap URLs:</div>
                            <div className="row-link-stack">
                              {effectiveSitemaps.sitemapUrls.map((url, idx) => (
                                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                                  {url}
                                </a>
                              ))}
                            </div>
                          </div>
                        ) : null
                      }
                    />
                  </div>
                </section>
              );
            })()}

            {/* Keyword-Content Analysis Section */}
            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Keyword-Content Analysis</h3>
                <CircularProgress percentage={calculateContentScore(results)} />
              </div>
              <div className="section-content">
                {(() => {
                  const contentWordCount = results.contentWordCount || 0;
                  const paragraphCount = results.auditResults?.content?.paragraphCount || 0;
                  const { remark: contentRemark, status: contentStatus } = getContentRemark({ wordCount: contentWordCount });
                  
                  return (
                    <>
                      <DetailRow
                        label="Content Word Count"
                        value={`${contentWordCount.toLocaleString()} words`}
                        remark={contentWordCount > 0
                          ? contentRemark + ' Longer, comprehensive content tends to rank better in search results.'
                          : 'No content word count available. This may indicate an issue with content extraction.'}
                        status={contentWordCount > 0 ? contentStatus : 'error'}
                      />
                      <DetailRow
                        label="Paragraphs"
                        value={`${paragraphCount} paragraph${paragraphCount === 1 ? '' : 's'}`}
                        remark="Break content into short paragraphs to keep readers engaged. Well-structured paragraphs improve readability and user experience."
                        status={paragraphCount > 0 ? 'good' : 'warning'}
                      />
                    </>
                  );
                })()}
              </div>
            </section>

            {/* Keyword-Heading Analysis Section */}
            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Keyword-Heading Analysis</h3>
                <CircularProgress percentage={calculateKeywordHeadingScore(results)} />
              </div>
              <div className="section-content">
                <DetailRow
                  label="Keyword in Headings"
                  value={results.inHeadings?.found ? `Yes (${results.inHeadings.locations.length} found)` : 'No'}
                  remark={results.inHeadings?.found
                    ? `Your keyword appears in ${results.inHeadings.locations.length} heading(s). Using keywords in headings helps search engines understand the structure and importance of your content.`
                    : 'Your keyword is not found in any headings. Consider using your target keyword in H1, H2, or H3 tags to help search engines understand your content better.'}
                  status={results.inHeadings?.found ? 'good' : 'warning'}
                  additionalContent={results.inHeadings?.found && results.inHeadings.locations.length > 0 ? (
                    <div className="heading-container">
                      <div className="additional-content-label">Heading Locations:</div>
                      <ul className="inline-list">
                        {results.inHeadings.locations.map((location, idx) => (
                          <li key={idx}><strong>{location.level}:</strong> {location.text}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                />
                {(() => {
                  const headings = results.auditResults?.headings || {};
                  const h1Count = headings.h1Count || 0;
                  const h2Count = headings.h2?.length || 0;
                  const locations = results.inHeadings?.locations || [];
                  const keywordInH1 = locations.some(loc => loc.level === 'H1');
                  const keywordInH2 = locations.some(loc => loc.level === 'H2');
                  
                  return (
                    <>
                      <DetailRow
                        label="H1 Heading"
                        value={h1Count === 0 ? 'Not found' : `${h1Count} detected`}
                        remark={
                          h1Count === 0
                            ? 'Your page is missing the main heading (H1 tag). This is the most important heading that tells both visitors and search engines what your page is about. Add one clear, descriptive H1 at the top of your main content.'
                            : h1Count > 1
                            ? `You have ${h1Count} main headings (H1 tags) on this page. It's best to have only one H1 per page to clearly show what the page is about. Remove the extra ones and use H2 tags for other important sections instead.`
                            : keywordInH1
                            ? 'Your page has one main heading (H1 tag) and it includes your keyword, which is ideal. This clearly tells search engines and visitors what the page is about.'
                            : 'Your page has one main heading (H1 tag), but it does not include your keyword. Consider updating the H1 to naturally include your target keyword so search engines better understand the topic of the page.'}
                        status={
                          h1Count === 0
                            ? 'error'
                            : h1Count > 1
                            ? 'warning'
                            : keywordInH1
                            ? 'good'
                            : 'warning'
                        }
                        additionalContent={
                          headings.h1?.length > 0 ? (
                            <div>
                              <div className="additional-content-label">Detected H1 Headings:</div>
                              {formatHeadings(headings.h1)}
                            </div>
                          ) : null
                        }
                      />
                      <DetailRow
                        label="H2 Headings"
                        value={h2Count === 0 ? 'Not found' : `${h2Count} detected`}
                        remark={
                          h2Count === 0
                            ? 'Your page doesn\'t have any section headings (H2 tags). These help organize your content into clear sections, making it easier for both readers and search engines to understand your page structure. Add H2 tags to break up your content.'
                            : keywordInH2
                            ? `Your page has ${h2Count} section headings (H2 tags), and at least one of them includes your keyword. This helps connect your content structure to your target topic.`
                            : `Your page has ${h2Count} section headings (H2 tags), which helps organize your content into clear sections. Consider including your keyword in one or more H2 tags so search engines better understand the main topics of each section.`}
                        status={h2Count === 0 ? 'warning' : keywordInH2 ? 'good' : 'warning'}
                        additionalContent={
                          headings.h2?.length > 0 ? (
                            <div>
                              <div className="additional-content-label">Detected H2 Headings:</div>
                              {formatHeadings(headings.h2)}
                            </div>
                          ) : null
                        }
                      />
                    </>
                  );
                })()}
              </div>
            </section>

            {/* Keyword Count & Density Analysis Section */}
            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Keyword Count & Density Analysis</h3>
                <CircularProgress percentage={calculateKeywordDensityScore(results.keywordDensity)} />
              </div>
              <div className="section-content">
                <DetailRow
                  label="Keyword Count"
                  value={results.keywordCount || 0}
                  remark={results.keywordCount > 0
                    ? `Your keyword appears ${results.keywordCount} time(s) in the content. This is a good sign that your content is relevant to the keyword. However, make sure the usage is natural and not over-optimized.`
                    : 'Your keyword does not appear in the page content. This is a critical issue - search engines need to see your keyword in the actual content to understand what your page is about.'}
                  status={results.keywordCount > 0 ? 'good' : 'error'}
                />
                <DetailRow
                  label="Keyword Density"
                  value={`${results.keywordDensity}%`}
                  remark={(() => {
                    const density = parseFloat(results.keywordDensity) || 0;
                    if (density >= 1 && density <= 2) {
                      return 'Perfect! Your keyword density is in the optimal range (1-2%). This indicates natural keyword usage without over-optimization.';
                    } else if (density > 0 && density < 1) {
                      return 'Critical: Your keyword density is below 1%, which is too low. This is a critical SEO issue. Consider naturally incorporating your keyword more times in the content to reach the optimal range of 1-2%.';
                    } else if (density > 2 && density < 3) {
                      return 'Your keyword density is slightly high (above 2%). Make sure your content reads naturally and isn\'t over-optimized.';
                    } else if (density >= 3) {
                      return 'Critical: Your keyword density is higher, which may be considered keyword stuffing. This can hurt your SEO and user experience. Focus on natural, user-friendly content and reduce keyword repetition. An optimal keyword density would be between 1%-2%';
                    } else {
                      return 'Critical: Your keyword density is 0%, meaning the keyword doesn\'t appear in the content. This is a critical SEO issue that needs to be addressed immediately.';
                    }
                  })()}
                  status={(() => {
                    const density = parseFloat(results.keywordDensity) || 0;
                    if (density >= 1 && density <= 2) return 'good';
                    if (density > 2 && density < 3) return 'warning';
                    if (density >= 3) return 'error';
                    if (density > 0 && density < 1) return 'error';
                    return 'error';
                  })()}
                />
              </div>
            </section>

            {/* Keyword-URL Analysis Section */}
            {!isHomepage(results.targetUrl) && (
              <section className="result-section">
                <div className="section-header">
                  <h3 className="section-title">Keyword-URL Analysis</h3>
                  <CircularProgress percentage={calculateUrlScore(results)} />
                </div>
                <div className="section-content">
                  <DetailRow
                    label="Keyword in URL"
                    value={results.inUrl ? 'Yes' : 'No'}
                    remark={results.inUrl
                      ? 'Excellent! Your keyword appears in the URL. URLs with keywords are more SEO-friendly and can help search engines understand what your page is about. This also makes URLs more user-friendly and clickable.'
                      : 'Your keyword is not found in the URL. Including your target keyword in the URL can improve SEO and make your URLs more descriptive and user-friendly. Consider updating your URL structure to include relevant keywords.'}
                    status={results.inUrl ? 'good' : 'warning'}
                  />
                  <DetailRow
                    label="Current URL"
                    value={results.targetUrl}
                    remark="This is the URL that was analyzed. If the keyword is not present, consider updating the URL structure to include your target keyword."
                    status={results.inUrl ? 'good' : 'warning'}
                  />
                </div>
              </section>
            )}

            {/* Keyword-Image Analysis Section */}
            <section className="result-section">
              <div className="section-header">
                <h3 className="section-title">Keyword-Image Analysis</h3>
                <CircularProgress percentage={calculateImageScore(results)} />
              </div>
              <div className="section-content">
                {(() => {
                  const images = results.auditResults?.images || {
                    total: results.totalImages || 0,
                    withAlt: 0,
                    withoutAlt: 0,
                    largeImages: 0,
                    largeImageList: [],
                    missingAltList: [],
                    missingWebpList: []
                  };
                  const totalImages = images.total || results.totalImages || 0;

                  const { remark: hygieneRemark, status: hygieneStatus } = getImageRemark(images);

                  return (
                    <>
                      <DetailRow
                        label="Total Images"
                        value={`${totalImages || 0} image${totalImages === 1 ? '' : 's'}`}
                        remark={
                          totalImages > 0
                            ? 'Total number of images found on the page. Images can enhance user experience and provide additional SEO opportunities through alt text.'
                            : 'No images found on this page. While not required, images can improve user engagement and provide SEO benefits through alt text.'
                        }
                        status={totalImages > 0 ? 'good' : 'warning'}
                      />
                      <DetailRow
                        label="Images with Keyword in Alt Text or Title"
                        value={results.imagesWithKeyword || 0}
                        remark={(() => {
                          if (totalImages === 0) {
                            return 'No images found on the page.';
                          } else if (results.imagesWithKeyword === 0) {
                            return 'None of your images have the keyword in their alt text or title attribute. Adding your target keyword to image alt text and titles can help with SEO and accessibility.';
                          } else if (results.imagesWithKeyword === 1) {
                            return 'One image has your keyword in its alt text or title. Consider adding the keyword to more image alt texts and titles for better SEO.';
                          } else {
                            return `${results.imagesWithKeyword} images have your keyword in their alt text or title. This is good for SEO and helps search engines understand the context of your images.`;
                          }
                        })()}
                        status={
                          results.imagesWithKeyword > 0
                            ? 'good'
                            : totalImages > 0
                            ? 'warning'
                            : 'warning'
                        }
                      />
                      {results.imagesWithKeyword > 0 && results.imagesWithKeywordList?.length > 0 && (
                        <div className="image-detail-row image-detail-good">
                          <div className="image-detail-content">
                            <div className="image-list-container">
                              <div className="image-preview-item image-preview-header">
                                <div className="image-preview-section image-preview-left">
                                  <span className="image-header-label">Image</span>
                                </div>
                                <div className="image-preview-section image-preview-info">
                                  <span className="image-header-label">Details</span>
                                </div>
                                <div className="image-preview-section image-preview-remark">
                                  <span className="image-header-label">Alt Text / Title</span>
                                </div>
                              </div>
                              {results.imagesWithKeywordList.map((img, idx) => {
                                const src = img.src || '';
                                const title = img.title || '';
                                const alt = img.alt || '';

                                return (
                                  <div key={idx} className="image-preview-item image-preview-row">
                                    <div className="image-preview-section image-preview-left">
                                      <img
                                        src={src}
                                        alt={alt || 'Preview'}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          if (e.target.nextSibling) {
                                            e.target.nextSibling.style.display = 'flex';
                                          }
                                        }}
                                        className="image-thumbnail"
                                      />
                                      <div className="image-placeholder" style={{ display: 'none' }}>
                                        <svg
                                          width="40"
                                          height="40"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        >
                                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                          <polyline points="21 15 16 10 5 21"></polyline>
                                        </svg>
                                      </div>
                                    </div>
                                    <div className="image-preview-section image-preview-info">
                                      {title && title.trim() && (
                                        <div className="image-title">{title}</div>
                                      )}
                                      {src && (
                                        <a
                                          href={src}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="image-link"
                                        >
                                          {src.length > 60 ? `${src.substring(0, 60)}...` : src}
                                        </a>
                                      )}
                                    </div>
                                    <div className="image-preview-section image-preview-remark">
                                      {alt && <div className="image-remark-label">Alt Text</div>}
                                      <p className="image-remark-text">{alt || 'No alt text'}</p>
                                      {title && title !== alt && (
                                        <>
                                          <div
                                            className="image-remark-label"
                                            style={{ marginTop: '12px' }}
                                          >
                                            Title
                                          </div>
                                          <p className="image-remark-text">{title}</p>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                      <DetailRow
                        label="Images Missing Alt Text"
                        value={
                          images.withoutAlt === 0
                            ? 'All images have alt text'
                            : `${images.withoutAlt} image${images.withoutAlt === 1 ? '' : 's'} missing alt text`
                        }
                        remark={hygieneRemark}
                        status={hygieneStatus}
                      />
                      {images.missingAltList?.length > 0 && (
                        <div className="image-detail-row image-detail-warning">
                          <div className="image-detail-content">
                            {formatImageList(images.missingAltList, {
                              remarkText: 'Add descriptive alt text for this image.'
                            })}
                          </div>
                        </div>
                      )}
                      <DetailRow
                        label="Large Images"
                        value={
                          images.largeImages === 0
                            ? 'No large images found'
                            : `${images.largeImages} image${images.largeImages === 1 ? '' : 's'} larger than 300KB`
                        }
                        remark={
                          images.largeImages === 0
                            ? 'All images are under 300KB.'
                            : 'Compress images larger than 300KB to improve performance.'
                        }
                        status={images.largeImages === 0 ? 'good' : 'warning'}
                      />
                      {images.largeImageList?.length > 0 && (
                        <div className="image-detail-row image-detail-warning">
                          <div className="image-detail-content">
                            {formatImageList(images.largeImageList, {
                              showSize: true,
                              remarkText: 'Compress this image below 300KB.'
                            })}
                          </div>
                        </div>
                      )}
                      <DetailRow
                        label="Images Missing WebP Version"
                        value={
                          images.missingWebpList?.length
                            ? `${images.missingWebpList.length} image${
                                images.missingWebpList.length === 1 ? '' : 's'
                              } missing WebP`
                            : 'All images have WebP versions'
                        }
                        remark={
                          images.missingWebpList?.length
                            ? 'Convert these images to WebP for faster loads.'
                            : 'Great! All images have modern WebP versions.'
                        }
                        status={images.missingWebpList?.length ? 'warning' : 'good'}
                      />
                      {images.missingWebpList?.length > 0 && (
                        <div className="image-detail-row image-detail-warning">
                          <div className="image-detail-content">
                            {formatImageList(images.missingWebpList, {
                              remarkText: 'Add a WebP version of this image.'
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </section>

            {/* Link Profile Analysis Section */}
            {(() => {
              const links = results.auditResults?.links;
              if (!links) return null;
              const totalLinks = links.total || 0;
              const internalLinks = links.internal || 0;
              const externalLinks = links.external || 0;
              const { remark, status } = getLinkRemark(links);

              return (
                <section className="result-section">
                  <div className="section-header">
                    <h3 className="section-title">Link Profile Analysis</h3>
                    <CircularProgress percentage={calculateAuditLinkScore(links)} />
                  </div>
                  <div className="section-content">
                    <DetailRow
                      label="Canonical URL"
                      value={
                        links.canonicalUrl ? (
                          <a
                            href={links.canonicalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-link"
                          >
                            {links.canonicalUrl}
                          </a>
                        ) : 'Not set'
                      }
                      remark={
                        links.canonicalUrl
                          ? 'Great — your canonical tag clearly tells search engines which URL is the master copy of this page. Make sure it always points to the preferred clean URL.'
                          : 'You haven\'t set a canonical tag. Add <link rel="canonical"> so search engines don\'t treat URL variations as duplicate content.'
                      }
                      status={links.canonicalUrl ? 'good' : 'warning'}
                    />
                    <DetailRow
                      label="Total Links"
                      value={`${totalLinks} links (${internalLinks} internal / ${externalLinks} external)`}
                      remark={remark}
                      status={status}
                    />
                    <DetailRow
                      label="Internal Links"
                      value={`${internalLinks} internal link${internalLinks === 1 ? '' : 's'}`}
                      remark={
                        internalLinks > 0
                          ? 'Internal links help visitors and crawlers discover more of your content. Keep adding contextual links that guide people deeper into the site.'
                          : 'No internal links were detected on this page. Link to related pages to strengthen site structure and pass authority between key URLs.'
                      }
                      status={internalLinks > 0 ? 'good' : 'warning'}
                    />
                    {links.internalList && links.internalList.length > 0 && (
                      <div className="link-detail-row">
                        <div className="link-detail-content">
                          {formatLinkList(links.internalList)}
                        </div>
                      </div>
                    )}
                    <DetailRow
                      label="External Links"
                      value={`${externalLinks} external link${externalLinks === 1 ? '' : 's'}`}
                      remark={
                        externalLinks > 0
                          ? 'External links to reputable sources boost credibility. Make sure they open in a new tab and use rel="nofollow" where appropriate.'
                          : 'You don\'t link out to other trusted resources on this page. Consider citing helpful references or partners when it adds value.'
                      }
                      status={externalLinks > 0 ? 'good' : 'warning'}
                    />
                    {links.externalList && links.externalList.length > 0 && (
                      <div className="link-detail-row">
                        <div className="link-detail-content">
                          {formatLinkList(links.externalList)}
                        </div>
                      </div>
                    )}
                    <DetailRow
                      label="Broken Links"
                      value={
                        links.broken?.length
                          ? `${links.broken.length} broken link${links.broken.length === 1 ? '' : 's'}`
                          : 'No broken links detected'
                      }
                      remark={
                        links.broken?.length
                          ? 'This URL seems to be a broken URL, kindly check it manually and fix or remove it so users and crawlers don’t hit dead ends.'
                          : 'All checked links responded successfully. Keep monitoring regularly to catch future issues.'
                      }
                      status={links.broken?.length ? 'error' : 'good'}
                    />
                    {links.broken && links.broken.length > 0 && (
                      <div className="link-detail-row link-detail-warning">
                        <div className="link-detail-content">
                          {formatLinkList(
                            links.broken.map((link) => ({
                              ...link,
                              tagLabelOverride: 'Broken',
                              tagClassOverride: 'tag-broken'
                            }))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              );
            })()}

            {/* Page Audit Results Section */}
            {results.auditResults && (
              <SiteAuditSections auditResults={results.auditResults} />
            )}
              </div>
            </div>
          );
        })()}

        {/* Add Keyword Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={handleCloseAddModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Add Keyword</h3>
                <button className="modal-close" onClick={handleCloseAddModal}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <form onSubmit={handleAddKeyword} className="modal-form">
                <div className="form-group">
                  <label htmlFor="modal-keyword">Keyword</label>
                  <input
                    type="text"
                    id="modal-keyword"
                    value={modalKeyword}
                    onChange={(e) => setModalKeyword(e.target.value)}
                    placeholder="Enter keyword to analyze"
                    className="keyword-input"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="modal-targetUrl">Target URL</label>
                  <input
                    type="text"
                    id="modal-targetUrl"
                    value={modalTargetUrl}
                    onChange={(e) => setModalTargetUrl(e.target.value)}
                    placeholder="Enter target URL (e.g., example.com or https://example.com)"
                    className="url-input"
                    disabled={loading}
                  />
                </div>
                {error && (
                  <div className="error-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
                <div className="modal-actions">
                  <button
                    type="button"
                    className="modal-cancel-btn"
                    onClick={handleCloseAddModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="modal-submit-btn" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner"></span>
                        Analyzing...
                      </>
                    ) : (
                      'Analyze Keyword'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={handleCancelDelete}>
            <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-dialog-header">
                <h3 className="confirm-dialog-title">Delete Keyword</h3>
                <button className="modal-close" onClick={handleCancelDelete}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="confirm-dialog-body">
                <p className="confirm-dialog-message">
                  Are you sure you want to delete the keyword <strong>"{deletingKeywordName}"</strong>?
                </p>
                <p className="confirm-dialog-warning">
                  This action cannot be undone.
                </p>
              </div>
              <div className="confirm-dialog-actions">
                <button
                  type="button"
                  className="confirm-dialog-cancel-btn"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="confirm-dialog-delete-btn"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      )}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          setShowUpgradeModal(false);
          window.open('https://parrotseo.com/pricing', '_blank', 'noopener,noreferrer');
        }}
      />
    </div>
  );
};

export default KeywordAnalysis;

const SiteAuditSections = ({ auditResults }) => {
  if (!auditResults) return null;

  // eslint-disable-next-line no-unused-vars
  const security = auditResults.security || {};
  // eslint-disable-next-line no-unused-vars
  const robots = auditResults.robots || {};
  // eslint-disable-next-line no-unused-vars
  const schema = auditResults.schema || { types: [] };
  // eslint-disable-next-line no-unused-vars
  const sitemaps = auditResults.sitemaps || { sitemapUrls: [] };
  // eslint-disable-next-line no-unused-vars
  const meta = auditResults.meta || {};
  // eslint-disable-next-line no-unused-vars
  const headings = auditResults.headings || {
    h1Count: 0,
    h1: [],
    h2: []
  };
  // eslint-disable-next-line no-unused-vars
  const content = auditResults.content || { wordCount: 0, paragraphCount: 0 };
  // eslint-disable-next-line no-unused-vars
  const images = auditResults.images || {
    total: 0,
    withAlt: 0,
    withoutAlt: 0,
    largeImages: 0,
    largeImageList: [],
    missingAltList: [],
    missingWebpList: []
  };
  // eslint-disable-next-line no-unused-vars
  const links = auditResults.links || { total: 0, internal: 0, external: 0 };

  return null;
};
